import { useState, useEffect, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import { ShieldCheck, Pencil, Trash2, Plus, Save, Activity, RefreshCw, Clock, Database, CheckCircle2, AlertTriangle, Zap, Info, FileSpreadsheet, Wifi, ArrowLeftRight, Target, Upload, Timer, PenLine, CalendarClock, Check, ChevronsUpDown, Image as ImageIcon, X, KeyRound, Mail, Send, Download, History, Eye, Search, RotateCcw, Copy, Wallet, Rocket, Settings2, Loader2, HeartHandshake } from 'lucide-react';
import api from '@/lib/api';
import { useGet } from '@/lib/useData';
import { useAuth } from '@/context/AuthContext';
import { useTenant } from '@/context/TenantContext';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionCard } from '@/components/common/SectionCard';
import { CardSkeleton } from '@/components/common/Loading';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { Checkbox } from '@/components/ui/checkbox';
import { ErrorState } from '@/components/common/ErrorState';
import { MultiSelect } from '@/components/layout/MultiSelect';
import { fmtInt, fmtDateTime } from '@/lib/format';

// Détection d'un code campagne "à séquence" : se termine par UNE lettre (H, I, …)
// après le motif AA+LLL+NN (ex. 26SITFA99H). Sert à afficher un badge « séquence X »
// qui clarifie les canaux paraissant dupliqués (même base, lettres de séquence
// différentes) sans jamais les fusionner.
const SEQ_CODE_RE = /^\d{2}[A-Z]{3,}\d{2}[A-Z]$/;
function seqInfo(code) {
  const c = String(code || '');
  if (SEQ_CODE_RE.test(c)) return { base: c.slice(0, -1), seq: c.slice(-1) };
  return { base: c, seq: '' };
}

// Badge indiquant l'accès d'un admin à un module réservé (Suivi Social / Plan stratégique).
// Cliquable (onToggle) pour activer/couper l'accès directement depuis la liste.
const AccessBadge = ({ icon: Icon, label, granted, testid, onToggle }) => {
  const cls = `gap-1 whitespace-nowrap text-[10px] ${granted
    ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
    : 'border-dashed text-muted-foreground opacity-70'}`;
  const inner = <><Icon className="h-3 w-3" /> {label}{granted ? '' : ' · off'}</>;
  if (!onToggle) {
    return <Badge variant="outline" data-testid={testid} data-granted={granted ? 'yes' : 'no'} className={cls}>{inner}</Badge>;
  }
  return (
    <button type="button" onClick={onToggle} data-testid={testid} data-granted={granted ? 'yes' : 'no'}
      title={granted ? "Cliquer pour couper l'accès" : "Cliquer pour activer l'accès"}
      className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)]">
      <Badge variant="outline" className={`${cls} cursor-pointer transition-transform hover:scale-[1.04]`}>{inner}</Badge>
    </button>
  );
};


export default function Admin() {
  const [tab, setTab] = useState('health');
  const [evgFocus, setEvgFocus] = useState(null);   // association à préselectionner dans Evergiving
  const goConfigure = (code) => { setEvgFocus(code); setTab('evergiving'); };
  const health = useGet('/admin/system/health');
  const stale = health.data?.stale_setups || [];
  return (
    <div className="space-y-6">
      <PageHeader title="Administration SHARITI" icon={ShieldCheck} subtitle="Back-office : santé des données, associations, utilisateurs, objectifs, API et logs" />
      {stale.length > 0 && (
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-950/40" data-testid="stale-setup-alert">
          <div className="flex items-start gap-3">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <div className="flex-1 space-y-2">
              <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                {stale.length === 1 ? '1 association créée depuis plus de 24 h sans aucune donnée' : `${stale.length} associations créées depuis plus de 24 h sans aucune donnée`}
              </p>
              <p className="text-xs text-amber-800/90 dark:text-amber-300/80">Branchez leur connexion Evergiving (campaign_id + schema_id) puis lancez une synchronisation.</p>
              <div className="flex flex-wrap gap-2 pt-0.5">
                {stale.map((s) => (
                  <Button key={s.association_code} size="sm" variant="outline"
                    className="h-7 gap-1.5 border-amber-400 bg-white/70 text-amber-900 hover:bg-white dark:bg-transparent dark:text-amber-200"
                    onClick={() => goConfigure(s.association_code)}
                    data-testid={`stale-configure-${s.association_code}`}>
                    <Settings2 className="h-3.5 w-3.5" /> {s.name} <span className="opacity-60">· {Math.round(s.hours_since_created)} h</span>
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="health" data-testid="admin-tab-health">Santé des données</TabsTrigger>
          <TabsTrigger value="associations" data-testid="admin-tab-associations">Associations</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users">Utilisateurs</TabsTrigger>
          <TabsTrigger value="objectives" data-testid="admin-tab-objectives">Objectifs</TabsTrigger>
          <TabsTrigger value="codes" data-testid="admin-tab-codes">Codes campagne</TabsTrigger>
          <TabsTrigger value="evergiving" data-testid="admin-tab-evergiving">Evergiving</TabsTrigger>
          <TabsTrigger value="api" data-testid="admin-tab-api">API Booking</TabsTrigger>
          <TabsTrigger value="hr" data-testid="admin-tab-hr">Heures RH</TabsTrigger>
          <TabsTrigger value="reporting" data-testid="admin-tab-reporting">Reporting Amnesty</TabsTrigger>
          <TabsTrigger value="logs" data-testid="admin-tab-logs">Logs</TabsTrigger>
        </TabsList>
        <TabsContent value="health" className="mt-4"><HealthTab onConfigure={goConfigure} /></TabsContent>
        <TabsContent value="associations" className="mt-4"><AssociationsTab /></TabsContent>
        <TabsContent value="users" className="mt-4"><UsersTab /></TabsContent>
        <TabsContent value="objectives" className="mt-4"><ObjectivesTab /></TabsContent>
        <TabsContent value="codes" className="mt-4"><CodesTab /></TabsContent>
        <TabsContent value="evergiving" className="mt-4"><EvergivingTab focusCode={evgFocus} /></TabsContent>
        <TabsContent value="api" className="mt-4"><ServiceTokensTab /></TabsContent>
        <TabsContent value="hr" className="mt-4"><HRHoursTab /></TabsContent>
        <TabsContent value="reporting" className="mt-4"><AmnestyReportTab /></TabsContent>
        <TabsContent value="logs" className="mt-4"><LogsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

const HEALTH_STATUS = {
  ok: { label: 'OK', cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  attention: { label: 'Attention', cls: 'bg-amber-100 text-amber-700', icon: AlertTriangle },
  critique: { label: 'Critique', cls: 'bg-rose-100 text-rose-700', icon: AlertTriangle },
};

function HealthTab({ onConfigure }) {
  const { data, isLoading, isError, refetch } = useGet('/admin/system/health');
  const { data: audit } = useGet('/admin/audit-log');

  if (isLoading) return <CardSkeleton />;
  if (isError) return <ErrorState body="Impossible de charger la santé des données." onRetry={refetch} />;

  const assocs = data?.associations || [];
  const warnings = data?.global_warnings || [];
  const allOk = warnings.length === 0;

  return (
    <div className="space-y-4">
      {/* Global banner */}
      <div
        className={`flex items-start gap-3 rounded-2xl border p-4 ${allOk ? 'bg-emerald-50/60' : 'bg-amber-50/60'}`}
        data-testid="health-banner"
      >
        {allOk
          ? <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
          : <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />}
        <div className="min-w-0">
          <p className="font-display text-sm font-semibold text-foreground">
            {allOk ? 'Toutes les sources de données sont saines' : `${warnings.length} alerte(s) de fraîcheur / cohérence`}
          </p>
          {allOk ? (
            <p className="text-xs text-muted-foreground break-words">
              Chaque association est servie depuis Evergiving live et reçoit des données récentes.
            </p>
          ) : (
            <ul className="mt-1 space-y-0.5">
              {warnings.map((w, i) => (
                <li key={i} className="break-words text-xs text-amber-800">• {w}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <SectionCard
        title="Santé des données par association"
        subtitle="Source active, fraîcheur (dernière donnée reçue) et cohérence auto-sync ↔ source"
        testid="health-table"
        action={
          <Button variant="ghost" size="sm" className="gap-1.5" onClick={refetch} data-testid="health-refresh">
            <RefreshCw className="h-4 w-4" /> Actualiser
          </Button>
        }
      >
        <div className="overflow-x-auto rounded-xl border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Association</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Auto-sync</TableHead>
                <TableHead>Dernière donnée</TableHead>
                <TableHead className="text-right">Ancienneté</TableHead>
                <TableHead className="text-right">Enreg.</TableHead>
                <TableHead>État</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assocs.map((r) => {
                const st = HEALTH_STATUS[r.status] || HEALTH_STATUS.ok;
                const StIcon = st.icon;
                const needsSetup = (r.record_count || 0) === 0 || r.status === 'critique';
                return (
                  <TableRow key={r.association_code} data-testid="health-row">
                    <TableCell className="break-words font-medium">{r.name}</TableCell>
                    <TableCell>
                      <Badge className={r.data_source === 'evergiving' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}>
                        {r.data_source === 'evergiving' ? 'Evergiving live' : 'Excel'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.auto_sync_enabled ? 'Activé' : 'Désactivé'}</TableCell>
                    <TableCell className="break-words text-sm">{r.last_data_date || '—'}</TableCell>
                    <TableCell className="text-right tabular text-sm">
                      {r.days_since_data === null || r.days_since_data === undefined ? '—' : `${fmtInt(r.days_since_data)} j`}
                    </TableCell>
                    <TableCell className="text-right tabular text-sm text-muted-foreground">{fmtInt(r.record_count)}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-medium ${st.cls}`}>
                        <StIcon className="h-3 w-3" />{st.label}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {(needsSetup || r.data_source === 'evergiving') && (
                        <Button
                          variant={needsSetup ? 'default' : 'ghost'} size="sm"
                          className={`h-7 gap-1.5 ${needsSetup ? 'text-white' : 'text-muted-foreground'}`}
                          style={needsSetup ? { background: 'var(--tenant-accent-500)' } : undefined}
                          onClick={() => onConfigure && onConfigure(r.association_code)}
                          data-testid={`health-configure-${r.association_code}`}>
                          <Settings2 className="h-3.5 w-3.5" /> {needsSetup ? 'Configurer' : 'Gérer'}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      {/* Audit log of data_source changes */}
      <SectionCard
        title="Journal d'audit — bascules de source"
        subtitle="Traçabilité de chaque changement Excel ↔ Evergiving (qui, quand, avant → après)"
        testid="audit-log"
      >
        {audit && audit.length ? (
          <div className="overflow-x-auto rounded-xl border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Date</TableHead>
                  <TableHead>Association</TableHead>
                  <TableHead>Changement</TableHead>
                  <TableHead>Par</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {audit.map((a) => (
                  <TableRow key={a.id} data-testid="audit-row">
                    <TableCell className="break-words text-sm">{fmtDateTime(a.ts)}</TableCell>
                    <TableCell className="break-words text-sm font-medium">{a.association_code}</TableCell>
                    <TableCell className="text-sm">
                      <span className="text-muted-foreground">{typeof a.before === 'object' ? JSON.stringify(a.before) : (a.before ?? '—')}</span>
                      <span className="mx-1">→</span>
                      <span className="font-medium text-foreground">{typeof a.after === 'object' ? JSON.stringify(a.after) : (a.after ?? '—')}</span>
                    </TableCell>
                    <TableCell className="break-words text-sm text-muted-foreground">{a.actor || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">Aucune bascule de source enregistrée.</p>
        )}
      </SectionCard>
    </div>
  );
}

function LogoThumb({ src, name, color, size = 'h-9 w-9' }) {
  if (src) {
    return (
      <div className={`${size} flex shrink-0 items-center justify-center overflow-hidden rounded-xl border bg-white p-1`}>
        <img src={src} alt={name || 'logo'} className="h-full w-full object-contain" data-testid="assoc-logo-thumb" />
      </div>
    );
  }
  const initials = (name || 'SH').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
  return (
    <div className={`${size} flex shrink-0 items-center justify-center rounded-xl font-display text-sm font-bold text-white`} style={{ background: color || '#EE722E' }} data-testid="assoc-logo-thumb">
      {initials}
    </div>
  );
}

const WIZ_CANAL_OPTIONS = ['Rue Paris IDF', 'Rue itinérante', 'Porte à Porte', 'Sites privés'];
const WIZ_REGION_OPTIONS = ['Auvergne-Rhône-Alpes', 'Bourgogne-Franche-Comté', 'Bretagne', 'Centre-Val de Loire', 'Corse', 'Grand Est', 'Hauts-de-France', 'Île-de-France', 'Normandie', 'Nouvelle-Aquitaine', 'Occitanie', 'Pays de la Loire', 'Provence-Alpes-Côte d’Azur', 'Guadeloupe', 'Martinique', 'Guyane', 'La Réunion', 'Mayotte', 'Nouvelle-Aquitaine / Occitanie', 'Itinérant (national)'];
const WIZ_STEPS = ['Identité', 'Evergiving', 'Code campagne', 'Accès'];

const wizGenPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#%';
  const cryptoObj = (typeof window !== 'undefined' && (window.crypto || window.msCrypto)) || null;
  let out = '';
  const arr = new Uint32Array(14);
  if (cryptoObj?.getRandomValues) { cryptoObj.getRandomValues(arr); for (let i = 0; i < 14; i++) out += chars[arr[i] % chars.length]; }
  else { for (let i = 0; i < 14; i++) out += chars[Math.floor(Math.random() * chars.length)]; }
  return out;
};

// Assistant guidé « Ajouter une association » : identité → config Evergiving →
// mapping du code campagne → espace d'accès. Chaque étape réutilise l'endpoint
// existant correspondant ; les étapes 2-4 sont facultatives (bouton « Passer »).
function NewAssociationWizard({ open, onOpenChange, onDone }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [createdCode, setCreatedCode] = useState(null);
  const [identity, setIdentity] = useState({ code: '', name: '', short_name: '', primary_color: '#EE722E', tagline: '' });
  const [evg, setEvg] = useState({ campaign_id: '', schema_id: '26075' });
  const [codeMap, setCodeMap] = useState({ code: '', canal: '', region: '' });
  const [access, setAccess] = useState({ create: true, name: '', email: '', password: wizGenPassword() });
  const [accountCreated, setAccountCreated] = useState(false);
  const [evgTesting, setEvgTesting] = useState(false);
  const [evgTest, setEvgTest] = useState(null);

  const reset = () => {
    setStep(0); setBusy(false); setCreatedCode(null); setAccountCreated(false);
    setIdentity({ code: '', name: '', short_name: '', primary_color: '#EE722E', tagline: '' });
    setEvg({ campaign_id: '', schema_id: '26075' });
    setEvgTest(null);
    setCodeMap({ code: '', canal: '', region: '' });
    setAccess({ create: true, name: '', email: '', password: wizGenPassword() });
  };

  const close = (o) => { if (!o) { onDone && onDone(); reset(); } onOpenChange(o); };

  // Étape 1 — crée (ou met à jour) l'identité, puis avance.
  const submitIdentity = async () => {
    const code = (identity.code || '').trim().toUpperCase();
    if (!code) { toast.error('Code association requis'); return; }
    if (!identity.name.trim()) { toast.error("Nom de l'association requis"); return; }
    setBusy(true);
    try {
      const payload = { ...identity, code, short_name: identity.short_name || identity.name };
      if (!createdCode) {
        await api.post('/admin/associations', payload);
        setCreatedCode(code);
        setAccess((a) => ({ ...a, name: a.name || `${identity.name} — Espace` }));
        toast.success(`Association « ${identity.name} » créée`);
      } else {
        await api.put(`/admin/associations/${createdCode}`, payload);
      }
      setStep(1);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Création impossible'); }
    setBusy(false);
  };

  const submitEvergiving = async (skip = false) => {
    if (skip || (!String(evg.campaign_id).trim() && !String(evg.schema_id).trim())) { setStep(2); return; }
    setBusy(true);
    try {
      await api.put(`/admin/evergiving/${createdCode}`, {
        campaign_id: String(evg.campaign_id).trim() || null,
        schema_id: String(evg.schema_id).trim() || null,
        data_source: 'evergiving',
        auto_sync_enabled: true,
      });
      // Bout en bout : on lance aussitôt une première synchronisation pour que les
      // données remontent sans étape supplémentaire.
      let synced = false;
      try {
        if (String(evg.campaign_id).trim() && String(evg.schema_id).trim()) {
          await api.post('/sync/run', { association: createdCode });
          synced = true;
        }
      } catch (_) { /* la synchro pourra être relancée depuis l'onglet Evergiving */ }
      toast.success(synced ? 'Connexion enregistrée · synchronisation lancée' : 'Configuration Evergiving enregistrée');
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Configuration Evergiving impossible'); }
    setBusy(false);
  };

  // Détection automatique : scanne les campagnes et remplit le campaign_id de l'association.
  const detectEvergiving = async () => {
    setEvgTesting(true); setEvgTest(null);
    try {
      const { data } = await api.post('/sync/discover', { count: 400, budget_seconds: 50 });
      const wantTokens = (identity.name || '').toLowerCase().split(/[^a-z0-9]+/).filter((t) => t.length >= 3);
      const match = (data.new || []).find((h) =>
        h.suggested_association?.code === createdCode
        || wantTokens.some((t) => (h.ong || '').toLowerCase().includes(t)));
      if (match) {
        setEvg((p) => ({ ...p, campaign_id: String(match.campaign_id), schema_id: String(match.schema_id) }));
        setEvgTest({ count: match.count, ongs: { [match.ong]: match.count }, codes: { [match.code_campagne]: match.count } });
        toast.success(`Campagne détectée : ${match.ong} (#${match.campaign_id})`);
      } else {
        const nb = (data.new || []).length;
        toast.message(nb ? `${nb} campagne(s) trouvée(s) mais aucune ne correspond au nom — saisissez le campaign_id ou utilisez l'onglet Evergiving.` : 'Aucune campagne détectée dans la fenêtre analysée.');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Détection impossible');
    }
    setEvgTesting(false);
  };

  // Lecture test (read-only) pour confirmer le campaign_id avant d'enregistrer.
  const testEvergiving = async () => {
    if (!String(evg.campaign_id).trim()) { toast.error('Renseignez le campaign_id à tester'); return; }
    setEvgTesting(true); setEvgTest(null);
    try {
      const { data } = await api.post('/sync/test', { campaign_id: evg.campaign_id, schema_id: evg.schema_id || 26075 });
      setEvgTest(data);
      const ong = Object.keys(data.ongs || {})[0];
      toast.success(data.count ? `Détecté : ${ong || '—'} · ${data.count} bulletin(s)` : 'Connexion OK · 0 bulletin');
    } catch (e) {
      setEvgTest({ error: e?.response?.data?.detail || 'Échec du test' });
      toast.error(e?.response?.data?.detail || 'Échec du test');
    }
    setEvgTesting(false);
  };

  const submitCode = async (skip = false) => {
    const code = (codeMap.code || '').trim().toUpperCase();
    if (skip || !code) { setStep(3); return; }
    setBusy(true);
    try {
      await api.post('/admin/campaign-codes', {
        code, association_code: createdCode, canal: codeMap.canal || null, region: codeMap.region || null,
      });
      toast.success(`Code « ${code} » rattaché à ${identity.name}`);
      setStep(3);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Mapping du code impossible'); }
    setBusy(false);
  };

  const submitAccess = async (skip = false) => {
    if (skip || !access.create) { close(false); return; }
    const email = (access.email || '').trim().toLowerCase();
    if (!email) { toast.error("Renseignez l'email de l'espace (ou passez cette étape)"); return; }
    setBusy(true);
    try {
      await api.post('/admin/users', {
        role: 'ASSOCIATION_USER', association_code: createdCode,
        name: access.name || identity.name, email, password: access.password,
      });
      setAccountCreated(true);
      toast.success(`Accès créé pour ${email} — communiquez le mot de passe manuellement`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Création de l'accès impossible"); setBusy(false); return; }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-xl" data-testid="new-assoc-wizard">
        <DialogHeader>
          <DialogTitle>Ajouter une association</DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center justify-between gap-1 pb-1">
          {WIZ_STEPS.map((label, i) => (
            <div key={label} className="flex flex-1 items-center gap-2 last:flex-none">
              <div className="flex items-center gap-2">
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition-colors ${i < step ? 'text-white' : i === step ? 'text-white ring-2 ring-offset-2 ring-[color:var(--tenant-accent-300)]' : 'bg-muted text-muted-foreground'}`}
                  style={i <= step ? { background: 'var(--tenant-accent-500)' } : {}}
                  data-testid={`wiz-step-dot-${i}`}
                >
                  {i < step ? <Check className="h-4 w-4" /> : i + 1}
                </span>
                <span className={`hidden text-xs font-medium sm:inline ${i === step ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
              </div>
              {i < WIZ_STEPS.length - 1 && <span className={`h-px flex-1 ${i < step ? 'bg-[color:var(--tenant-accent-400)]' : 'bg-border'}`} />}
            </div>
          ))}
        </div>

        {/* Step content */}
        <div className="space-y-3 py-1">
          {step === 0 && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Code association *">
                  <Input value={identity.code} onChange={(e) => setIdentity({ ...identity, code: e.target.value.toUpperCase().replace(/\s/g, '') })}
                    placeholder="PLAN" disabled={!!createdCode} className="font-mono" data-testid="wiz-assoc-code" />
                </Field>
                <Field label="Couleur">
                  <div className="flex gap-2">
                    <Input value={identity.primary_color} onChange={(e) => setIdentity({ ...identity, primary_color: e.target.value })} data-testid="wiz-assoc-color" />
                    <input type="color" value={identity.primary_color || '#EE722E'} onChange={(e) => setIdentity({ ...identity, primary_color: e.target.value })} className="h-9 w-12 rounded-lg border" />
                  </div>
                </Field>
              </div>
              <Field label="Nom de l'association *">
                <Input value={identity.name} onChange={(e) => setIdentity({ ...identity, name: e.target.value })} placeholder="Plan International" data-testid="wiz-assoc-name" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Nom court"><Input value={identity.short_name} onChange={(e) => setIdentity({ ...identity, short_name: e.target.value })} placeholder="Plan Intl" data-testid="wiz-assoc-short" /></Field>
                <Field label="Slogan"><Input value={identity.tagline} onChange={(e) => setIdentity({ ...identity, tagline: e.target.value })} placeholder="…" data-testid="wiz-assoc-tagline" /></Field>
              </div>
              <p className="text-[11px] text-muted-foreground">Le logo s'ajoute ensuite via le crayon dans la liste. Le code doit être unique (ex. PLAN).</p>
            </>
          )}

          {step === 1 && (
            <>
              <p className="text-xs text-muted-foreground">
                Pour que les bulletins remontent depuis <b>Evergiving</b>, renseignez le <b>campaign_id</b> puis cliquez
                {' '}<b>Tester</b> : l'assistant lit un lot et vous confirme l'association détectée. Le <b>schema_id</b> est
                {' '}pré-rempli (<b className="font-mono">26075</b>, formulaire partagé).
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Field label="campaign_id"><Input value={evg.campaign_id} onChange={(e) => setEvg({ ...evg, campaign_id: e.target.value })} placeholder="ex. 7351" data-testid="wiz-evg-campaign" /></Field>
                <Field label="schema_id"><Input value={evg.schema_id} onChange={(e) => setEvg({ ...evg, schema_id: e.target.value })} placeholder="26075" data-testid="wiz-evg-schema" /></Field>
              </div>
              <Button type="button" variant="outline" className="w-full gap-2" onClick={testEvergiving} disabled={evgTesting} data-testid="wiz-evg-test">
                {evgTesting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />} Tester la connexion
              </Button>
              {evgTest && !evgTest.error && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 text-[11px] text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200" data-testid="wiz-evg-test-ok">
                  <p className="flex items-center gap-1.5 font-semibold"><CheckCircle2 className="h-4 w-4" /> {fmtInt(evgTest.count)} bulletin(s) · {Object.keys(evgTest.ongs || {}).join(', ') || '—'}</p>
                  <p className="mt-0.5">Code(s) : <b className="font-mono">{Object.keys(evgTest.codes || {}).join(', ') || '—'}</b></p>
                </div>
              )}
              {evgTest?.error && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-[11px] text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300" data-testid="wiz-evg-test-err">
                  <p className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" /> {evgTest.error}</p>
                </div>
              )}
              <div className="flex items-start gap-2 rounded-lg border border-sky-200 bg-sky-50 p-2.5 text-[11px] text-sky-900 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-200">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>Astuce : les campaign_id se suivent (ex. 5163, 6020, 7095…). Essayez un numéro proche de vos autres campagnes et cliquez Tester jusqu'à voir la bonne ONG. Laissez vide pour configurer plus tard.</span>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-xs text-muted-foreground">Rattachez le <b>code campagne</b> de l'association à un canal et une région, pour que les données soient correctement classées partout.</p>
              <Field label="Code campagne"><Input value={codeMap.code} onChange={(e) => setCodeMap({ ...codeMap, code: e.target.value.toUpperCase().replace(/\s/g, '') })} placeholder="STSHJIFFY27" className="font-mono" data-testid="wiz-code-code" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Canal">
                  <Select value={codeMap.canal} onValueChange={(v) => setCodeMap({ ...codeMap, canal: v })}>
                    <SelectTrigger data-testid="wiz-code-canal"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>{WIZ_CANAL_OPTIONS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label="Région">
                  <Select value={codeMap.region} onValueChange={(v) => setCodeMap({ ...codeMap, region: v })}>
                    <SelectTrigger data-testid="wiz-code-region"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                    <SelectContent>{WIZ_REGION_OPTIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              {accountCreated ? (
                <div className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950/40">
                  <p className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800 dark:text-emerald-300"><CheckCircle2 className="h-4 w-4" /> Accès créé</p>
                  <p className="text-xs text-emerald-900/90 dark:text-emerald-200/90">Communiquez ces identifiants manuellement (aucun email n'a été envoyé) :</p>
                  <div className="flex items-center justify-between gap-2 rounded-md bg-background/70 px-2.5 py-1.5 text-xs">
                    <span className="font-mono break-all">{access.email}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 rounded-md bg-background/70 px-2.5 py-1.5 text-xs">
                    <span className="font-mono">{access.password}</span>
                    <Button type="button" size="sm" variant="ghost" className="h-6 gap-1 px-2" onClick={() => { navigator.clipboard?.writeText(access.password); toast.success('Mot de passe copié'); }} data-testid="wiz-copy-password"><Copy className="h-3.5 w-3.5" /> Copier</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between rounded-lg border bg-muted/30 px-3 py-2">
                    <div><p className="text-sm font-medium">Créer l'espace d'accès</p><p className="text-[11px] text-muted-foreground">Un compte « utilisateur association » pour que {identity.name || "l'association"} accède à son espace.</p></div>
                    <Switch checked={access.create} onCheckedChange={(v) => setAccess({ ...access, create: v })} data-testid="wiz-access-toggle" />
                  </div>
                  {access.create && (
                    <>
                      <Field label="Nom affiché"><Input value={access.name} onChange={(e) => setAccess({ ...access, name: e.target.value })} placeholder={`${identity.name} — Espace`} data-testid="wiz-access-name" /></Field>
                      <Field label="Email de connexion *"><Input type="email" value={access.email} onChange={(e) => setAccess({ ...access, email: e.target.value })} placeholder="contact@plan…" data-testid="wiz-access-email" /></Field>
                      <Field label="Mot de passe">
                        <div className="flex gap-2">
                          <Input value={access.password} onChange={(e) => setAccess({ ...access, password: e.target.value })} className="font-mono" data-testid="wiz-access-password" />
                          <Button type="button" variant="outline" size="sm" className="shrink-0 gap-1.5" onClick={() => setAccess({ ...access, password: wizGenPassword() })} data-testid="wiz-access-genpass"><KeyRound className="h-4 w-4" /> Générer</Button>
                        </div>
                      </Field>
                      <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground"><Info className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Aucun email d'invitation n'est envoyé : transmettez l'email + le mot de passe vous-même.</p>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-row items-center justify-between gap-2 sm:justify-between">
          <div>
            {step > 0 && !accountCreated && (
              <Button type="button" variant="ghost" onClick={() => setStep(step - 1)} disabled={busy} data-testid="wiz-prev">Précédent</Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            {accountCreated ? (
              <Button type="button" className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} onClick={() => close(false)} data-testid="wiz-close-done"><CheckCircle2 className="h-4 w-4" /> Terminer</Button>
            ) : (
              <>
                {step >= 1 && step <= 3 && (
                  <Button type="button" variant="outline" disabled={busy}
                    onClick={() => (step === 1 ? submitEvergiving(true) : step === 2 ? submitCode(true) : submitAccess(true))}
                    data-testid="wiz-skip">Passer</Button>
                )}
                {step === 0 && <Button type="button" className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} disabled={busy} onClick={submitIdentity} data-testid="wiz-next-identity">{busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : null} Créer et continuer</Button>}
                {step === 1 && <Button type="button" className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} disabled={busy} onClick={() => submitEvergiving(false)} data-testid="wiz-next-evg">{busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : null} Suivant</Button>}
                {step === 2 && <Button type="button" className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} disabled={busy} onClick={() => submitCode(false)} data-testid="wiz-next-code">{busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : null} Suivant</Button>}
                {step === 3 && <Button type="button" className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} disabled={busy} onClick={() => submitAccess(false)} data-testid="wiz-finish">{busy ? <RefreshCw className="h-4 w-4 animate-spin" /> : null} Créer l'accès & terminer</Button>}
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssociationsTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useGet('/admin/associations');
  const [edit, setEdit] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const save = async () => {
    try {
      await api.put(`/admin/associations/${edit.code}`, edit);
      toast.success('Association mise à jour');
      setEdit(null);
      qc.invalidateQueries();
    } catch { toast.error('Échec de la mise à jour'); }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f || !edit) return;
    if (f.size > 3 * 1024 * 1024) { toast.error('Fichier trop volumineux (max 3 Mo)'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data: updated } = await api.post(`/admin/associations/${edit.code}/logo`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setEdit((prev) => ({ ...prev, logo_url: updated.logo_url }));
      toast.success('Logo téléversé');
      qc.invalidateQueries();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Échec du téléversement');
    } finally { setUploading(false); }
  };

  if (isLoading) return <CardSkeleton />;
  return (
    <SectionCard title="Associations" subtitle="Identité et branding de chaque partenaire" bodyClassName="!px-0">
      <div className="flex justify-end px-4 pb-3">
        <Button className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} onClick={() => setWizardOpen(true)} data-testid="add-association-btn">
          <Plus className="h-4 w-4" /> Ajouter une association
        </Button>
      </div>
      <NewAssociationWizard open={wizardOpen} onOpenChange={setWizardOpen} onDone={() => qc.invalidateQueries()} />
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            <TableHead>Logo</TableHead><TableHead>Association</TableHead><TableHead>Code</TableHead><TableHead>Couleur</TableHead><TableHead>Slogan</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(data || []).map((a) => (
              <TableRow key={a.code} data-testid="admin-association-row">
                <TableCell><LogoThumb src={a.logo_url} name={a.name} color={a.primary_color} /></TableCell>
                <TableCell className="font-medium break-words">{a.name}</TableCell>
                <TableCell className="font-mono text-xs">{a.code}</TableCell>
                <TableCell><span className="inline-flex items-center gap-1.5"><span className="h-4 w-4 rounded-full border" style={{ background: a.primary_color }} /><span className="font-mono text-xs">{a.primary_color}</span></span></TableCell>
                <TableCell className="max-w-[280px] break-words text-sm text-muted-foreground">{a.tagline}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => setEdit({ ...a })} data-testid="edit-association-btn"><Pencil className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="break-words">Modifier {edit?.name}</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <Field label="Logo de l'association">
                <div className="flex items-center gap-3 rounded-xl border bg-muted/30 p-3">
                  <LogoThumb src={edit.logo_url} name={edit.name} color={edit.primary_color} size="h-14 w-14" />
                  <div className="flex flex-1 flex-wrap items-center gap-2">
                    <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden" onChange={onFile} data-testid="assoc-logo-file-input" />
                    <Button type="button" size="sm" variant="outline" className="gap-1.5" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="assoc-logo-upload-btn">
                      {uploading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      {uploading ? 'Téléversement…' : 'Téléverser'}
                    </Button>
                    {edit.logo_url ? (
                      <Button type="button" size="sm" variant="ghost" className="gap-1.5 text-rose-500 hover:text-rose-600" onClick={() => setEdit({ ...edit, logo_url: '' })} data-testid="assoc-logo-remove-btn">
                        <X className="h-4 w-4" /> Retirer
                      </Button>
                    ) : null}
                  </div>
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground break-words">PNG, JPG, WEBP ou SVG · 3 Mo max · redimensionné automatiquement.</p>
              </Field>
              <Field label="URL du logo (alternative à l'upload)"><Input value={(edit.logo_url && edit.logo_url.startsWith('data:')) ? '' : (edit.logo_url || '')} onChange={(e) => setEdit({ ...edit, logo_url: e.target.value })} placeholder="https://…" data-testid="assoc-logo-url-input" /></Field>
              <Field label="Nom"><Input value={edit.name || ''} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="assoc-name-input" /></Field>
              <Field label="Nom court"><Input value={edit.short_name || ''} onChange={(e) => setEdit({ ...edit, short_name: e.target.value })} /></Field>
              <Field label="Couleur (hex)"><div className="flex gap-2"><Input value={edit.primary_color || ''} onChange={(e) => setEdit({ ...edit, primary_color: e.target.value })} data-testid="assoc-color-input" /><input type="color" value={edit.primary_color || '#0B7A75'} onChange={(e) => setEdit({ ...edit, primary_color: e.target.value })} className="h-9 w-12 rounded-lg border" /></div></Field>
              <Field label="Slogan"><Input value={edit.tagline || ''} onChange={(e) => setEdit({ ...edit, tagline: e.target.value })} /></Field>

              {/* Segmentation « parrainage » : réservé aux associations qui font du parrainage
                  (ex. Plan Intl). Active la ventilation dons réguliers / parrainage dans le pilotage. */}
              <div className="space-y-3 rounded-xl border bg-muted/30 p-3" data-testid="assoc-parrainage-block">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-start gap-2">
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg accent-bg-100">
                      <HeartHandshake className="h-4 w-4 accent-text" />
                    </span>
                    <div>
                      <p className="text-sm font-medium text-foreground">Activité parrainage</p>
                      <p className="text-[11px] text-muted-foreground">Ventile les dons réguliers et les parrainages dans l'espace de pilotage de l'association.</p>
                    </div>
                  </div>
                  <Switch
                    checked={!!edit.parrainage?.enabled}
                    onCheckedChange={(v) => setEdit({ ...edit, parrainage: { seuil: edit.parrainage?.seuil ?? 30, enabled: v } })}
                    data-testid="assoc-parrainage-toggle"
                  />
                </div>
                {edit.parrainage?.enabled && (
                  <Field label="Seuil de parrainage (€ / mois)">
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        type="number" min="1" step="1" inputMode="numeric"
                        value={edit.parrainage?.seuil ?? 30}
                        onChange={(e) => setEdit({ ...edit, parrainage: { enabled: true, seuil: e.target.value === '' ? '' : Number(e.target.value) } })}
                        className="max-w-[130px]" data-testid="assoc-parrainage-seuil"
                      />
                      <span className="flex-1 text-[11px] text-muted-foreground">Un don <b>≥ {Number(edit.parrainage?.seuil) || 30} €</b> compte comme <b>parrainage</b> · en-dessous = <b>don régulier</b>.</span>
                    </div>
                  </Field>
                )}
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={save} className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="save-association-btn"><Save className="h-4 w-4" /> Enregistrer</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

const genPassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789@#%';
  const cryptoObj = (typeof window !== 'undefined' && (window.crypto || window.msCrypto)) || null;
  let out = '';
  if (cryptoObj?.getRandomValues) {
    const arr = new Uint32Array(14);
    cryptoObj.getRandomValues(arr);
    for (let i = 0; i < 14; i++) out += chars[arr[i] % chars.length];
  } else {
    for (let i = 0; i < 14; i++) out += chars[Math.floor(Math.random() * chars.length)];
  }
  return out;
};

function UsersTab() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  // SÉCURITÉ : SEUL le propriétaire (administrateur total) peut voir et gérer l'accès
  // aux modules confidentiels. Un admin non-propriétaire ne voit même pas ces contrôles.
  const isOwner = me?.is_owner === true;
  const meCanManageSocial = isOwner;
  const meCanManageStrategy = isOwner;
  const { data, isLoading } = useGet('/admin/users');
  const assoc = useGet('/admin/associations');
  const respDir = useGet('/admin/responsables-directory');
  const [open, setOpen] = useState(false);
  const emptyForm = { name: '', email: '', role: 'ASSOCIATION_USER', association_code: '', scope_responsables: [], scope_canaux: [], password: 'demo2026', can_view_social: false, can_view_strategy: false };
  const [form, setForm] = useState(emptyForm);
  const [edit, setEdit] = useState(null); // user being edited
  const [saving, setSaving] = useState(false);

  const respOptions = (respDir.data || []).map((r) => ({
    value: r.responsable,
    label: r.responsable,
    sublabel: `${(r.association_names || []).join(' · ')} · ${r.records} enreg.`,
    keywords: `${r.responsable} ${(r.association_names || []).join(' ')}`,
  }));
  // Canal options limited to the channels the selected responsable(s) actually have.
  const canalOptionsFor = (selectedResp) => {
    const sel = new Set(selectedResp || []);
    const set = new Set();
    (respDir.data || []).forEach((r) => { if (sel.has(r.responsable)) (r.canaux || []).forEach((c) => set.add(c)); });
    return [...set].sort().map((c) => ({ value: c, label: c }));
  };
  const roleLabel = (role) => (role === 'SHARITI_ADMIN' ? 'Admin'
    : role === 'CAMPAIGN_MANAGER' ? 'Chargé de campagne' : 'Association');

  const create = async () => {
    if (form.role === 'ASSOCIATION_USER' && !form.association_code) { toast.error('Sélectionnez une association'); return; }
    if (form.role === 'CAMPAIGN_MANAGER' && !form.scope_responsables.length) { toast.error('Sélectionnez au moins un responsable'); return; }
    try {
      await api.post('/admin/users', form);
      toast.success('Utilisateur créé');
      setOpen(false); setForm(emptyForm); qc.invalidateQueries();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
  };
  const del = async (id) => { try { await api.delete(`/admin/users/${id}`); toast.success('Supprimé'); qc.invalidateQueries(); } catch { toast.error('Échec'); } };
  const toggleAccess = async (u, field) => {
    const granted = u[field] === true;
    try {
      await api.put(`/admin/users/${u.id}`, { [field]: !granted });
      toast.success(!granted ? 'Accès activé' : 'Accès coupé');
      qc.invalidateQueries();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de la mise à jour'); }
  };
  const openEdit = (u) => setEdit({ id: u.id, name: u.name || '', email: u.email || '', role: u.role, association_code: u.association_code || '', scope_responsables: u.scope_responsables || [], scope_canaux: u.scope_canaux || [], password: '', can_view_social: u.can_view_social === true, can_view_strategy: u.can_view_strategy === true });
  const saveEdit = async () => {
    if (edit.role === 'ASSOCIATION_USER' && !edit.association_code) { toast.error('Sélectionnez une association'); return; }
    if (edit.role === 'CAMPAIGN_MANAGER' && !edit.scope_responsables.length) { toast.error('Sélectionnez au moins un responsable'); return; }
    setSaving(true);
    try {
      const payload = { name: edit.name, email: edit.email, role: edit.role, association_code: edit.association_code, scope_responsables: edit.scope_responsables, scope_canaux: edit.scope_canaux, can_view_social: edit.can_view_social, can_view_strategy: edit.can_view_strategy };
      if (edit.password) payload.password = edit.password;
      await api.put(`/admin/users/${edit.id}`, payload);
      toast.success('Utilisateur mis à jour');
      setEdit(null); qc.invalidateQueries();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de la mise à jour'); }
    finally { setSaving(false); }
  };
  if (isLoading) return <CardSkeleton />;
  return (
    <SectionCard title="Utilisateurs" bodyClassName="!px-0"
      action={<Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild><Button size="sm" className="gap-1.5 text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="add-user-btn"><Plus className="h-4 w-4" /> Ajouter</Button></DialogTrigger>
        <DialogContent>
          <DialogHeader><DialogTitle>Nouvel utilisateur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Field label="Nom"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></Field>
            <Field label="Email"><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></Field>
            <Field label="Rôle">
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ASSOCIATION_USER">Utilisateur association</SelectItem>
                  <SelectItem value="CAMPAIGN_MANAGER">Chargé de campagne</SelectItem>
                  <SelectItem value="SHARITI_ADMIN">Admin SHARITI</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            {form.role === 'SHARITI_ADMIN' && meCanManageSocial && (
              <Field label="Accès Suivi Social">
                <div className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2">
                  <span className="text-xs text-muted-foreground break-words">Module back-office confidentiel (coûts, paie…). <strong>Désactivé par défaut</strong> — activez pour accorder l'accès à cet admin.</span>
                  <Switch checked={form.can_view_social} onCheckedChange={(v) => setForm({ ...form, can_view_social: v })} data-testid="user-social-switch" />
                </div>
              </Field>
            )}
            {form.role === 'SHARITI_ADMIN' && meCanManageStrategy && (
              <Field label="Accès Plan stratégique">
                <div className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2">
                  <span className="text-xs text-muted-foreground break-words">Tableau de bord stratégique (chantiers, KPIs…). <strong>Désactivé par défaut</strong> — activez pour donner l'accès à cet admin.</span>
                  <Switch checked={form.can_view_strategy} onCheckedChange={(v) => setForm({ ...form, can_view_strategy: v })} data-testid="user-strategy-switch" />
                </div>
              </Field>
            )}
            {form.role === 'ASSOCIATION_USER' && (
              <Field label="Association">
                <Select value={form.association_code} onValueChange={(v) => setForm({ ...form, association_code: v })}>
                  <SelectTrigger data-testid="user-assoc-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{(assoc.data || []).map((a) => <SelectItem key={a.code} value={a.code}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
            )}
            {form.role === 'CAMPAIGN_MANAGER' && (
              <Field label="Responsable(s) — périmètre de l'équipe">
                <MultiSelect label="Choisir un responsable" testid="user-scope-select" searchable
                  searchPlaceholder="Rechercher un responsable…"
                  options={respOptions} selected={form.scope_responsables}
                  onChange={(v) => setForm({ ...form, scope_responsables: v, scope_canaux: [] })} />
                <p className="mt-1.5 text-[11px] text-muted-foreground break-words">
                  Le chargé de campagne ne verra que ses propres statistiques et celles de son équipe (associations où il a travaillé).
                </p>
              </Field>
            )}
            {form.role === 'CAMPAIGN_MANAGER' && form.scope_responsables.length > 0 && (
              <Field label="Canal (optionnel)">
                <MultiSelect label="Tous les canaux" testid="user-canal-select"
                  options={canalOptionsFor(form.scope_responsables)} selected={form.scope_canaux}
                  onChange={(v) => setForm({ ...form, scope_canaux: v })} />
                <p className="mt-1.5 text-[11px] text-muted-foreground break-words">
                  Laissez vide pour tous ses canaux. Sinon il ne verra que le(s) canal(x) choisi(s) — ex. « Sites privés » uniquement (pas le porte-à-porte).
                </p>
              </Field>
            )}
            <Field label="Mot de passe">
              <div className="flex gap-2">
                <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" />
                <Button type="button" variant="outline" size="sm" className="shrink-0 gap-1.5" onClick={() => setForm({ ...form, password: genPassword() })} data-testid="user-genpass-btn"><KeyRound className="h-4 w-4" /> Générer</Button>
              </div>
            </Field>
          </div>
          <DialogFooter><Button onClick={create} className="text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="create-user-btn">Créer</Button></DialogFooter>
        </DialogContent>
      </Dialog>}>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead>Rôle</TableHead><TableHead>Périmètre</TableHead><TableHead>Accès modules</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {(data || []).map((u) => (
              <TableRow key={u.id} data-testid="admin-user-row">
                <TableCell className="font-medium break-words">{u.name}</TableCell>
                <TableCell className="text-sm break-words">{u.email}</TableCell>
                <TableCell><Badge variant="secondary" className="rounded-full">{roleLabel(u.role)}</Badge></TableCell>
                <TableCell className="font-mono text-xs break-words">
                  {u.role === 'CAMPAIGN_MANAGER'
                    ? (u.scope_responsables?.length
                        ? `${u.scope_responsables.join(', ')}${u.scope_canaux?.length ? ` · ${u.scope_canaux.join(', ')}` : ' · tous canaux'}`
                        : '—')
                    : (u.association_code || '—')}
                </TableCell>
                <TableCell>
                  {(u.role === 'SHARITI_ADMIN' && isOwner) ? (
                    <div className="flex flex-wrap gap-1">
                      <AccessBadge icon={Wallet} label="Suivi Social" granted={u.can_view_social === true}
                        onToggle={(meCanManageSocial && u.id !== me?.id) ? () => toggleAccess(u, 'can_view_social') : undefined}
                        testid={`user-social-access-${u.id}`} />
                      <AccessBadge icon={Rocket} label="Plan stratégique" granted={u.can_view_strategy === true}
                        onToggle={(meCanManageStrategy && u.id !== me?.id) ? () => toggleAccess(u, 'can_view_strategy') : undefined}
                        testid={`user-strategy-access-${u.id}`} />
                    </div>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid="edit-user-btn"><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => del(u.id)} data-testid="delete-user-btn"><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="break-words">Modifier l'utilisateur</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <Field label="Nom"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-user-name-input" /></Field>
              <Field label="Email (identifiant de connexion)"><Input value={edit.email} onChange={(e) => setEdit({ ...edit, email: e.target.value })} data-testid="edit-user-email-input" /></Field>
              <Field label="Rôle">
                <Select value={edit.role} onValueChange={(v) => setEdit({ ...edit, role: v })}>
                  <SelectTrigger data-testid="edit-user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ASSOCIATION_USER">Utilisateur association</SelectItem>
                    <SelectItem value="CAMPAIGN_MANAGER">Chargé de campagne</SelectItem>
                    <SelectItem value="SHARITI_ADMIN">Admin SHARITI</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {edit.role === 'SHARITI_ADMIN' && meCanManageSocial && edit.id !== me?.id && (
                <Field label="Accès Suivi Social">
                  <div className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2">
                    <span className="text-xs text-muted-foreground break-words">Module back-office (coûts, paie…). Décochez pour le masquer à cet admin.</span>
                    <Switch checked={edit.can_view_social} onCheckedChange={(v) => setEdit({ ...edit, can_view_social: v })} data-testid="edit-user-social-switch" />
                  </div>
                </Field>
              )}
              {edit.role === 'SHARITI_ADMIN' && meCanManageStrategy && edit.id !== me?.id && (
                <Field label="Accès Plan stratégique">
                  <div className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2">
                    <span className="text-xs text-muted-foreground break-words">Tableau de bord stratégique (chantiers, KPIs…). Décochez pour le masquer à cet admin.</span>
                    <Switch checked={edit.can_view_strategy} onCheckedChange={(v) => setEdit({ ...edit, can_view_strategy: v })} data-testid="edit-user-strategy-switch" />
                  </div>
                </Field>
              )}
              {edit.role === 'ASSOCIATION_USER' && (
                <Field label="Association">
                  <Select value={edit.association_code} onValueChange={(v) => setEdit({ ...edit, association_code: v })}>
                    <SelectTrigger data-testid="edit-user-assoc-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                    <SelectContent>{(assoc.data || []).map((a) => <SelectItem key={a.code} value={a.code}>{a.name}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              )}
              {edit.role === 'CAMPAIGN_MANAGER' && (
                <Field label="Responsable(s) — périmètre de l'équipe">
                  <MultiSelect label="Choisir un responsable" testid="edit-user-scope-select" searchable
                    searchPlaceholder="Rechercher un responsable…"
                    options={respOptions} selected={edit.scope_responsables}
                    onChange={(v) => setEdit({ ...edit, scope_responsables: v, scope_canaux: [] })} />
                </Field>
              )}
              {edit.role === 'CAMPAIGN_MANAGER' && edit.scope_responsables.length > 0 && (
                <Field label="Canal (optionnel)">
                  <MultiSelect label="Tous les canaux" testid="edit-user-canal-select"
                    options={canalOptionsFor(edit.scope_responsables)} selected={edit.scope_canaux}
                    onChange={(v) => setEdit({ ...edit, scope_canaux: v })} />
                </Field>
              )}
              <Field label="Nouveau mot de passe">
                <div className="flex gap-2">
                  <Input value={edit.password} onChange={(e) => setEdit({ ...edit, password: e.target.value })} placeholder="Laisser vide pour conserver" data-testid="edit-user-password-input" />
                  <Button type="button" variant="outline" size="sm" className="shrink-0 gap-1.5" onClick={() => setEdit({ ...edit, password: genPassword() })} data-testid="edit-user-genpass-btn"><KeyRound className="h-4 w-4" /> Réinitialiser</Button>
                </div>
                <p className="mt-1.5 text-[11px] text-muted-foreground break-words">Laissez vide pour conserver le mot de passe actuel. Le bouton « Réinitialiser » génère un mot de passe fort affiché en clair.</p>
              </Field>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} disabled={saving} className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="save-user-btn"><Save className="h-4 w-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}

const PERIOD_TYPES = [
  { value: 'year', label: 'Année' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'month', label: 'Mois' },
  { value: 'custom', label: 'Personnalisée' },
];

const emptyObjForm = (code, shortName) => ({
  scope_type: 'association',
  scope_value: code || '',
  period_type: 'year',
  period_key: '2026',
  target_bs_reel: 1000,
  weekly_target: '',
  label: `Objectif annuel ${shortName || ''} 2026`.trim(),
  period_start: '2026-01-01',
  period_end: '2026-12-31',
});

function ObjectivesTab() {
  const qc = useQueryClient();
  const assoc = useGet('/admin/associations');
  const usersQ = useGet('/admin/users');
  const cmUsers = useMemo(
    () => (usersQ.data || []).filter((u) => u.role === 'CAMPAIGN_MANAGER'),
    [usersQ.data]);
  const cmNameById = useMemo(() => {
    const m = {};
    cmUsers.forEach((u) => { m[u.id] = u.name || u.email || u.id; });
    return m;
  }, [cmUsers]);
  const [current, setCurrent] = useState(null);
  const code = current || assoc.data?.[0]?.code;
  const shortName = (assoc.data || []).find((a) => a.code === code)?.short_name || code;
  const { data, refetch } = useGet('/objectives', { association: code }, !!code);
  const [dialog, setDialog] = useState(null); // { mode: 'create'|'edit', form }
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const openCreate = () => setDialog({ mode: 'create', form: emptyObjForm(code, shortName) });
  const openEdit = (o) => setDialog({ mode: 'edit', form: { ...o, weekly_target: o.weekly_target ?? '', user_id: o.user_id ?? (o.scope_type === 'campaign_manager' ? o.scope_value : undefined) } });
  const patchForm = (patch) => setDialog((d) => ({ ...d, form: { ...d.form, ...patch } }));

  // When picking a period type, prefill dates from the given "period_key" for convenience.
  const applyPeriodType = (period_type) => {
    const f = dialog.form;
    let { period_start, period_end, period_key } = f;
    const yr = (String(period_key || '').match(/\d{4}/) || ['2026'])[0];
    if (period_type === 'year') {
      period_key = yr; period_start = `${yr}-01-01`; period_end = `${yr}-12-31`;
    }
    patchForm({ period_type, period_key, period_start, period_end });
  };

  const save = async () => {
    const f = dialog.form;
    if (!f.target_bs_reel || Number(f.target_bs_reel) <= 0) { toast.error('Cible invalide'); return; }
    if (!f.period_start || !f.period_end) { toast.error('Renseignez les dates de période'); return; }
    const isCM = f.scope_type === 'campaign_manager';
    if (isCM && !f.user_id) { toast.error('Sélectionnez un chargé de campagne'); return; }
    setSaving(true);
    try {
      const payload = {
        ...f,
        association_code: code,
        scope_value: f.scope_type === 'association' ? code : (isCM ? f.user_id : (f.scope_value || '')),
        user_id: isCM ? f.user_id : null,
      };
      if (dialog.mode === 'create') {
        await api.post('/objectives', payload);
        toast.success('Objectif créé');
      } else {
        await api.put(`/objectives/${f.id}`, payload);
        toast.success('Objectif mis à jour');
      }
      setDialog(null);
      refetch();
      qc.invalidateQueries();
    } catch { toast.error('Échec de l\u2019enregistrement'); } finally { setSaving(false); }
  };

  const del = async () => {
    try {
      await api.delete(`/objectives/${toDelete.id}`);
      toast.success('Objectif supprimé');
      setToDelete(null);
      refetch();
      qc.invalidateQueries();
    } catch { toast.error('Échec'); }
  };

  const f = dialog?.form;
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3 rounded-xl border bg-muted/40 p-3 text-xs text-muted-foreground" data-testid="objectives-help">
        <Target className="mt-0.5 h-4 w-4 shrink-0 accent-text" />
        <span>Définissez ici les <strong className="text-foreground">objectifs internes SHARITI</strong> (cible de BS validés + période, cible hebdomadaire optionnelle) par <strong className="text-foreground">association</strong>, par <strong className="text-foreground">chargé de campagne</strong> (objectif personnel propre à son équipe) ou par <strong className="text-foreground">code campagne</strong>. Ils alimentent directement la page <strong className="text-foreground">Objectifs</strong> et le suivi hebdomadaire.</span>
      </div>
      <SectionCard
        title="Objectifs configurables"
        subtitle="Paramétrez et ajustez vos cibles à tout moment"
        bodyClassName="!px-0"
        action={
          <div className="flex items-center gap-2">
            <Select value={code || ''} onValueChange={setCurrent}>
              <SelectTrigger className="w-[170px]" data-testid="obj-assoc-select"><SelectValue /></SelectTrigger>
              <SelectContent>{(assoc.data || []).map((a) => <SelectItem key={a.code} value={a.code}>{a.short_name}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" className="gap-1.5 text-white" style={{ background: 'var(--tenant-accent-500)' }} onClick={openCreate} data-testid="add-objective-btn"><Plus className="h-4 w-4" /> Nouvel objectif</Button>
          </div>
        }>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              <TableHead>Libellé</TableHead>
              <TableHead>Portée</TableHead>
              <TableHead>Période</TableHead>
              <TableHead className="text-right">Cible (BS validés)</TableHead>
              <TableHead className="text-right">Cible hebdo</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {(data || []).map((o) => (
                <TableRow key={o.id} data-testid="admin-objective-row">
                  <TableCell className="font-medium">{o.label}</TableCell>
                  <TableCell className="text-xs">{
                    o.scope_type === 'association' ? 'Association'
                      : o.scope_type === 'campaign_manager'
                        ? <>Chargé de campagne · <span className="font-medium text-foreground">{o.cm_name || cmNameById[o.user_id || o.scope_value] || o.scope_value}</span></>
                        : <>Code campagne · <span className="font-mono">{o.scope_value}</span></>
                  }</TableCell>
                  <TableCell className="text-xs">{o.period_start} → {o.period_end}</TableCell>
                  <TableCell className="text-right tabular font-semibold" data-testid={`obj-target-${o.id}`}>{fmtInt(o.target_bs_reel)}</TableCell>
                  <TableCell className="text-right tabular">{o.weekly_target ? fmtInt(o.weekly_target) : '—'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(o)} data-testid="edit-objective-btn"><Pencil className="h-4 w-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => setToDelete(o)} data-testid="delete-objective-btn"><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                  </TableCell>
                </TableRow>
              ))}
              {(!data || data.length === 0) && <TableRow><TableCell colSpan={6} className="py-6 text-center text-sm text-muted-foreground">Aucun objectif pour cette association. Cliquez sur « Nouvel objectif ».</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      {/* Create / Edit dialog */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="objective-dialog">
          <DialogHeader><DialogTitle>{dialog?.mode === 'edit' ? 'Modifier l\u2019objectif' : 'Nouvel objectif'} — {shortName}</DialogTitle></DialogHeader>
          {f && (
            <div className="space-y-3">
              <Field label="Libellé"><Input value={f.label} onChange={(e) => patchForm({ label: e.target.value })} placeholder="Objectif annuel 2026" data-testid="obj-label-input" /></Field>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Portée">
                  <Select value={f.scope_type} onValueChange={(v) => patchForm({ scope_type: v, scope_value: v === 'campaign_manager' ? '' : f.scope_value, user_id: v === 'campaign_manager' ? f.user_id : undefined })}>
                    <SelectTrigger data-testid="obj-scope-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="association">Association</SelectItem>
                      <SelectItem value="campaign_manager">Chargé de campagne</SelectItem>
                      <SelectItem value="campaign_code">Code campagne</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Type de période">
                  <Select value={f.period_type} onValueChange={applyPeriodType}>
                    <SelectTrigger data-testid="obj-period-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{PERIOD_TYPES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              {f.scope_type === 'campaign_manager' && (
                <Field label="Chargé de campagne">
                  <Select value={f.user_id || ''} onValueChange={(v) => {
                    const nm = cmNameById[v] || '';
                    patchForm({ user_id: v, label: (!f.label || /^Objectif( annuel)?/.test(f.label)) ? `Objectif ${nm}`.trim() : f.label });
                  }}>
                    <SelectTrigger data-testid="obj-cm-select"><SelectValue placeholder="Sélectionner un chargé de campagne" /></SelectTrigger>
                    <SelectContent>
                      {cmUsers.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">Aucun chargé de campagne. Créez-en un dans l'onglet « Utilisateurs ».</div>}
                      {cmUsers.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name || u.email}{u.scope_responsables?.length ? ` · ${u.scope_responsables.join(', ')}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              )}
              {f.scope_type === 'campaign_code' && <Field label="Code campagne"><Input value={f.scope_value} onChange={(e) => patchForm({ scope_value: e.target.value })} className="font-mono" placeholder="ex: AM-PAR-01" data-testid="obj-scope-value-input" /></Field>}
              <div className="grid grid-cols-2 gap-2">
                <Field label="Début"><Input type="date" value={f.period_start} onChange={(e) => patchForm({ period_start: e.target.value })} data-testid="obj-start-input" /></Field>
                <Field label="Fin"><Input type="date" value={f.period_end} onChange={(e) => patchForm({ period_end: e.target.value })} data-testid="obj-end-input" /></Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Cible (BS validés)"><Input type="number" min="1" value={f.target_bs_reel} onChange={(e) => patchForm({ target_bs_reel: e.target.value })} data-testid="obj-target-input" /></Field>
                <Field label="Cible hebdomadaire (optionnel)"><Input type="number" min="0" value={f.weekly_target} onChange={(e) => patchForm({ weekly_target: e.target.value })} placeholder="auto" data-testid="obj-weekly-input" /></Field>
              </div>
              <p className="text-xs text-muted-foreground">Si la cible hebdomadaire est vide, elle est calculée automatiquement (cible ÷ nombre de semaines de la période).</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} disabled={saving}>Annuler</Button>
            <Button onClick={save} disabled={saving} className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="save-objective-btn"><Save className="h-4 w-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="objective-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet objectif ?</AlertDialogTitle>
            <AlertDialogDescription>L'objectif « {toDelete?.label} » ({fmtInt(toDelete?.target_bs_reel || 0)} BS validés) sera définitivement supprimé.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); del(); }} className="bg-rose-600 text-white hover:bg-rose-700" data-testid="confirm-delete-objective">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// --- Codes campagne (back-office CRUD) ------------------------------------
const CANAL_FALLBACK = ['Rue Paris IDF', 'Rue itinérante', 'Porte à Porte', 'Sites privés'];
const CUSTOM_OPT = '__custom__';
const CODE_SOURCE_BADGE = {
  override: { label: 'Modifié', cls: 'bg-emerald-100 text-emerald-700' },
  auto: { label: 'Auto', cls: 'bg-sky-100 text-sky-700' },
  unmapped: { label: 'À compléter', cls: 'bg-amber-100 text-amber-700' },
};

// Select suggestions + free-text escape hatch ("Autre / personnalisé").
function ComboField({ value, onChange, options, placeholder, testid }) {
  const known = options.includes(value);
  const [custom, setCustom] = useState(!!value && !known);
  const selectVal = custom ? CUSTOM_OPT : (known ? value : undefined);
  return (
    <div className="space-y-2">
      <Select
        value={selectVal}
        onValueChange={(v) => {
          if (v === CUSTOM_OPT) { setCustom(true); onChange(''); }
          else { setCustom(false); onChange(v); }
        }}
      >
        <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent className="max-h-72">
          {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
          <SelectItem value={CUSTOM_OPT}>Autre (personnalisé)…</SelectItem>
        </SelectContent>
      </Select>
      {custom && (
        <Input
          autoFocus
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Saisir une valeur personnalisée"
          data-testid={`${testid}-custom`}
        />
      )}
    </div>
  );
}

function CodeSourceBadge({ source }) {
  const b = CODE_SOURCE_BADGE[source] || CODE_SOURCE_BADGE.unmapped;
  return <Badge variant="secondary" className={`rounded-full ${b.cls}`}>{b.label}</Badge>;
}

// Shared add/edit form fields. Defined at module level (NOT inside CodesTab) so it
// keeps a stable identity across renders — otherwise inputs would lose focus.
function CodeFields({ state, setState, lockCode, associations, canalOptions, regionOptions }) {
  return (
    <div className="space-y-3">
      <Field label="Code campagne">
        <Input
          value={state.code}
          disabled={lockCode}
          onChange={(e) => setState({ ...state, code: e.target.value })}
          placeholder="ex : 26SFXDA13A"
          className="font-mono uppercase"
          data-testid="code-input"
        />
      </Field>
      <Field label="Association">
        <Select value={state.association_code} onValueChange={(v) => setState({ ...state, association_code: v })}>
          <SelectTrigger data-testid="code-assoc-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
          <SelectContent>{(associations || []).map((a) => <SelectItem key={a.code} value={a.code}>{a.name}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      <Field label="Canal">
        <ComboField
          value={state.canal}
          onChange={(v) => setState({ ...state, canal: v })}
          options={canalOptions}
          placeholder="Choisir un canal"
          testid="code-canal-select"
        />
      </Field>
      <Field label="Région">
        <ComboField
          value={state.region}
          onChange={(v) => setState({ ...state, region: v })}
          options={regionOptions}
          placeholder="Choisir une région"
          testid="code-region-select"
        />
      </Field>
      <Field label="Responsable">
        <Input
          value={state.responsable}
          onChange={(e) => setState({ ...state, responsable: e.target.value })}
          placeholder="ex : MAXENCE R."
          data-testid="code-resp-input"
        />
      </Field>
    </div>
  );
}

function CodesTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useGet('/admin/campaign-codes');
  const assocQ = useGet('/admin/associations');
  const [search, setSearch] = useState('');
  const [assocFilter, setAssocFilter] = useState('all');
  const [srcFilter, setSrcFilter] = useState('all');
  const [open, setOpen] = useState(false);
  const emptyForm = { code: '', association_code: '', region: '', canal: '', responsable: '' };
  const [form, setForm] = useState(emptyForm);
  const [edit, setEdit] = useState(null);
  const [resetTarget, setResetTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const items = data?.items || [];
  const canalOptions = data?.canal_options || CANAL_FALLBACK;
  const regionOptions = data?.region_options || [];
  const associations = assocQ.data || [];
  const attentionCount = items.filter((it) => it.source === 'unmapped').length;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (assocFilter !== 'all' && it.association_code !== assocFilter) return false;
      if (srcFilter !== 'all' && it.source !== srcFilter) return false;
      if (!q) return true;
      return [it.code, it.region, it.canal, it.responsable, it.association_code]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
  }, [items, search, assocFilter, srcFilter]);

  const create = async () => {
    if (!form.code.trim()) { toast.error('Code campagne requis'); return; }
    if (!form.association_code) { toast.error('Association requise'); return; }
    setSaving(true);
    try {
      await api.post('/admin/campaign-codes', { ...form, code: form.code.trim().toUpperCase() });
      toast.success('Code campagne créé');
      setOpen(false); setForm(emptyForm); qc.invalidateQueries();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de la création'); }
    finally { setSaving(false); }
  };

  const openEdit = (it) => setEdit({
    code: it.code,
    association_code: it.association_code || '',
    region: it.region === 'Non renseigné' ? '' : (it.region || ''),
    canal: it.canal === 'Non renseigné' ? '' : (it.canal || ''),
    responsable: it.responsable || '',
    source: it.source,
    records: it.records,
    manual_override: it.manual_override,
  });

  const saveEdit = async () => {
    if (!edit.association_code) { toast.error('Association requise'); return; }
    setSaving(true);
    try {
      await api.put(`/admin/campaign-codes/${encodeURIComponent(edit.code)}`, {
        association_code: edit.association_code,
        region: edit.region,
        canal: edit.canal,
        responsable: edit.responsable,
      });
      toast.success('Code campagne mis à jour');
      setEdit(null); qc.invalidateQueries();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de la mise à jour'); }
    finally { setSaving(false); }
  };

  const doReset = async () => {
    if (!resetTarget) return;
    try {
      await api.delete(`/admin/campaign-codes/${encodeURIComponent(resetTarget.code)}`);
      toast.success('Code revenu à la détection automatique');
      setResetTarget(null); qc.invalidateQueries();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
  };

  if (isLoading) return <CardSkeleton />;

  return (
    <SectionCard
      title="Codes campagne"
      subtitle={`${items.length} codes${attentionCount ? ` · ${attentionCount} à compléter` : ''} — personnalisez association, région, canal et responsable`}
      bodyClassName="!px-0"
      action={
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) setForm(emptyForm); }}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5 text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="add-code-btn">
              <Plus className="h-4 w-4" /> Ajouter un code
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau code campagne</DialogTitle></DialogHeader>
            <CodeFields state={form} setState={setForm} lockCode={false} associations={associations} canalOptions={canalOptions} regionOptions={regionOptions} />
            <DialogFooter>
              <Button onClick={create} disabled={saving} className="text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="create-code-btn">
                {saving ? 'Création…' : 'Créer'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {/* Filters row */}
      <div className="flex flex-col gap-3 px-6 pb-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un code, une région, un responsable…"
            className="pl-9"
            data-testid="code-search-input"
          />
        </div>
        <Select value={assocFilter} onValueChange={setAssocFilter}>
          <SelectTrigger className="sm:w-48" data-testid="code-assoc-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les associations</SelectItem>
            {associations.map((a) => <SelectItem key={a.code} value={a.code}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={srcFilter} onValueChange={setSrcFilter}>
          <SelectTrigger className="sm:w-44" data-testid="code-source-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les états</SelectItem>
            <SelectItem value="unmapped">À compléter</SelectItem>
            <SelectItem value="override">Modifié</SelectItem>
            <SelectItem value="auto">Auto</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Code</TableHead>
              <TableHead>Association</TableHead>
              <TableHead>Région</TableHead>
              <TableHead>Canal</TableHead>
              <TableHead>Responsable</TableHead>
              <TableHead>État</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground" data-testid="code-empty-state">Aucun code ne correspond à votre recherche.</TableCell></TableRow>
            )}
            {filtered.map((c) => (
              <TableRow key={`${c.association_code}-${c.code}`} data-testid="admin-code-row">
                <TableCell className="font-mono text-xs font-semibold accent-text">{c.code}</TableCell>
                <TableCell className="text-sm">{c.association_code}</TableCell>
                <TableCell className={`text-sm ${c.region === 'Non renseigné' ? 'text-muted-foreground italic' : ''}`}>{c.region}</TableCell>
                <TableCell className={`text-sm ${c.canal === 'Non renseigné' ? 'text-muted-foreground italic' : ''}`}>{c.canal}</TableCell>
                <TableCell className="text-sm">{c.responsable || '—'}</TableCell>
                <TableCell><CodeSourceBadge source={c.source} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(c)} data-testid="edit-code-btn"><Pencil className="h-4 w-4" /></Button>
                    {c.manual_override && (
                      <Button variant="ghost" size="sm" title="Revenir à la détection automatique" onClick={() => setResetTarget(c)} data-testid="reset-code-btn"><RotateCcw className="h-4 w-4 text-amber-600" /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Edit dialog */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="break-words">Modifier le code <span className="font-mono">{edit?.code}</span></DialogTitle></DialogHeader>
          {edit && (
            <>
              {edit.records === 0 && (
                <p className="rounded-lg bg-sky-50 px-3 py-2 text-[11px] text-sky-700">Aucune donnée rattachée à ce code pour le moment.</p>
              )}
              <CodeFields state={edit} setState={setEdit} lockCode associations={associations} canalOptions={canalOptions} regionOptions={regionOptions} />
            </>
          )}
          <DialogFooter>
            <Button onClick={saveEdit} disabled={saving} className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="save-code-btn">
              <Save className="h-4 w-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset confirmation */}
      <AlertDialog open={!!resetTarget} onOpenChange={(o) => !o && setResetTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revenir à la détection automatique ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le code <span className="font-mono">{resetTarget?.code}</span> reprendra son canal et sa région détectés automatiquement (ou « Non renseigné » s'il n'est pas reconnu). La correction manuelle sera supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="reset-code-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doReset} data-testid="reset-code-confirm">Confirmer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SectionCard>
  );
}

const INTERVAL_OPTIONS = [
  { value: 1, label: 'Temps réel (~1 min)' },
  { value: 2, label: 'Toutes les 2 min' },
  { value: 5, label: 'Toutes les 5 min' },
  { value: 15, label: 'Toutes les 15 min' },
  { value: 30, label: 'Toutes les 30 min' },
  { value: 60, label: 'Toutes les heures' },
  { value: 120, label: 'Toutes les 2 heures' },
  { value: 360, label: 'Toutes les 6 heures' },
  { value: 720, label: 'Toutes les 12 heures' },
  { value: 1440, label: 'Une fois par jour' },
];

// --- API Booking — Jetons de service --------------------------------------
function ServiceTokensTab() {
  const qc = useQueryClient();
  const { data, isLoading } = useGet('/admin/service-tokens');
  const [name, setName] = useState('');
  const [openGen, setOpenGen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [generated, setGenerated] = useState(null); // secret affiché une seule fois
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [copied, setCopied] = useState(false);

  const tokens = data || [];
  const apiBase = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
  const endpoint = `${apiBase}/api/service/v1/bulletins`;
  const endpointPaie = `${apiBase}/api/service/v1/paie-releves`;

  const invalidate = () => qc.invalidateQueries({ predicate: (q) => String(q.queryKey?.[0] || '').startsWith('/admin/service-tokens') });

  const create = async () => {
    setCreating(true);
    try {
      const { data: res } = await api.post('/admin/service-tokens', { name });
      setGenerated(res);
      setOpenGen(false); setName('');
      invalidate();
      toast.success('Jeton généré — copiez-le maintenant, il ne sera plus affiché');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de la génération'); }
    finally { setCreating(false); }
  };

  const copy = async (txt) => {
    try {
      await navigator.clipboard.writeText(txt);
      setCopied(true); setTimeout(() => setCopied(false), 1800);
      toast.success('Copié dans le presse-papiers');
    } catch { toast.error('Copie impossible — sélectionnez et copiez manuellement'); }
  };

  const revoke = async () => {
    if (!revokeTarget) return;
    try {
      await api.delete(`/admin/service-tokens/${revokeTarget.id}`);
      toast.success('Jeton révoqué'); setRevokeTarget(null); invalidate();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec de la révocation'); }
  };

  if (isLoading) return <CardSkeleton />;

  return (
    <div className="space-y-4">
      {/* Documentation de l'endpoint */}
      <SectionCard title="API Booking — Bulletins (lecture seule)" subtitle="Endpoint machine-to-machine que Booking interroge pour récupérer les bulletins de dons">
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="rounded-md bg-emerald-100 text-emerald-700">GET</Badge>
            <code className="rounded-lg bg-muted px-2 py-1 text-xs" data-testid="api-endpoint-url">{endpoint}</code>
            <Button variant="ghost" size="sm" onClick={() => copy(endpoint)} data-testid="copy-endpoint-btn"><Copy className="h-4 w-4" /></Button>
          </div>
          <p className="text-muted-foreground">
            Authentification : en-tête <code className="rounded bg-muted px-1">Authorization: Bearer &lt;JETON&gt;</code>.
            Paramètres optionnels : <code className="rounded bg-muted px-1">date_from</code>, <code className="rounded bg-muted px-1">date_to</code> (YYYY-MM-DD), <code className="rounded bg-muted px-1">since</code> (ISO, nouveautés), <code className="rounded bg-muted px-1">limit</code> (max 5000).
          </p>
          <p className="text-[11px] text-muted-foreground">
            Confidentialité : aucune identité de donateur, ni adresse complète, ni email/téléphone. Géographie limitée à la ville. Réponse : <code className="rounded bg-muted px-1">{'{ items: [...], total: N }'}</code>.
          </p>
        </div>
      </SectionCard>

      {/* Documentation de l'endpoint Paie indés */}
      <SectionCard title="API Booking — Paie des indépendants (lecture seule)" subtitle="Endpoint que Booking interroge pour afficher l'onglet « Ma paie » de l'indépendant dans son espace existant">
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="rounded-md bg-emerald-100 text-emerald-700">GET</Badge>
            <code className="rounded-lg bg-muted px-2 py-1 text-xs" data-testid="api-paie-endpoint-url">{endpointPaie}?code_recruteur=AT0000</code>
            <Button variant="ghost" size="sm" onClick={() => copy(`${endpointPaie}?code_recruteur=AT0000`)} data-testid="copy-paie-endpoint-btn"><Copy className="h-4 w-4" /></Button>
          </div>
          <p className="text-muted-foreground">
            Authentification : en-tête <code className="rounded bg-muted px-1">Authorization: Bearer &lt;JETON&gt;</code> (le même jeton de service ci-dessous).
            Paramètres : <code className="rounded bg-muted px-1">code_recruteur</code> (obligatoire, = matricule), <code className="rounded bg-muted px-1">limit</code> (optionnel, défaut 24).
          </p>
          <p className="text-[11px] text-muted-foreground">
            Ne renvoie que les mois <strong>publiés</strong> et si l'indé est <strong>autorisé</strong> (« Peut voir sa paie »). Sinon <code className="rounded bg-muted px-1">{'{ visible: false, releves: [] }'}</code>. Liste blanche stricte : semaines, montant, total, période, date de prélèvement — aucune donnée d'un autre indé.
          </p>
        </div>
      </SectionCard>

      {/* Secret affiché une seule fois */}
      {generated && (
        <div className="rounded-2xl border-2 border-emerald-300 bg-emerald-50 p-4" data-testid="generated-token-box">
          <div className="flex items-start gap-2">
            <KeyRound className="mt-0.5 h-5 w-5 text-emerald-700" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-emerald-900">Jeton « {generated.name} » créé</h4>
              <p className="mt-0.5 text-xs text-emerald-800">Copiez-le maintenant : il ne sera <strong>plus jamais</strong> affiché. En cas de perte, révoquez-le et générez-en un nouveau.</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 overflow-x-auto rounded-lg border bg-white px-3 py-2 text-xs" data-testid="generated-token-value">{generated.token}</code>
                <Button size="sm" className="gap-1.5 text-white" style={{ background: 'var(--tenant-accent-500)' }} onClick={() => copy(generated.token)} data-testid="copy-token-btn">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? 'Copié' : 'Copier'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setGenerated(null)} data-testid="dismiss-token-btn"><X className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des jetons */}
      <SectionCard
        title="Jetons de service"
        subtitle={`${tokens.length} jeton(s) — révocables à tout moment`}
        bodyClassName="!px-0"
        action={
          <Dialog open={openGen} onOpenChange={(o) => { setOpenGen(o); if (o) setName(''); }}>
            <DialogTrigger asChild>
              <Button size="sm" className="gap-1.5 text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="generate-token-btn">
                <Plus className="h-4 w-4" /> Générer un jeton
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nouveau jeton de service</DialogTitle></DialogHeader>
              <Field label="Nom (pour vous y retrouver)">
                <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="ex : Booking Production" data-testid="token-name-input" />
              </Field>
              <DialogFooter>
                <Button onClick={create} disabled={creating} className="text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="create-token-btn">
                  {creating ? 'Génération…' : 'Générer'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      >
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Nom</TableHead>
                <TableHead>Préfixe</TableHead>
                <TableHead>Créé le</TableHead>
                <TableHead>Créé par</TableHead>
                <TableHead>Dernière utilisation</TableHead>
                <TableHead>État</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {tokens.length === 0 && (
                <TableRow><TableCell colSpan={7} className="py-10 text-center text-sm text-muted-foreground" data-testid="tokens-empty-state">Aucun jeton. Générez-en un pour connecter Booking.</TableCell></TableRow>
              )}
              {tokens.map((t) => (
                <TableRow key={t.id} data-testid="service-token-row">
                  <TableCell className="text-sm font-medium">{t.name}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{t.prefix}…</TableCell>
                  <TableCell className="text-xs">{t.created_at ? new Date(t.created_at).toLocaleString('fr-FR') : '—'}</TableCell>
                  <TableCell className="text-xs">{t.created_by || '—'}</TableCell>
                  <TableCell className="text-xs">{t.last_used_at ? new Date(t.last_used_at).toLocaleString('fr-FR') : 'Jamais'}</TableCell>
                  <TableCell>
                    {t.revoked
                      ? <Badge variant="secondary" className="rounded-full bg-rose-100 text-rose-700">Révoqué</Badge>
                      : <Badge variant="secondary" className="rounded-full bg-emerald-100 text-emerald-700">Actif</Badge>}
                  </TableCell>
                  <TableCell className="text-right">
                    {!t.revoked && (
                      <Button variant="ghost" size="sm" className="gap-1.5 text-rose-600" onClick={() => setRevokeTarget(t)} data-testid="revoke-token-btn">
                        <Trash2 className="h-4 w-4" /> Révoquer
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </SectionCard>

      <AlertDialog open={!!revokeTarget} onOpenChange={(o) => !o && setRevokeTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Révoquer ce jeton ?</AlertDialogTitle>
            <AlertDialogDescription>
              Le jeton « {revokeTarget?.name} » cessera immédiatement de fonctionner. Toute intégration qui l'utilise (ex. Booking) devra recevoir un nouveau jeton. Cette action est définitive.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="revoke-token-cancel">Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={revoke} className="bg-rose-600 hover:bg-rose-700" data-testid="revoke-token-confirm">Révoquer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function EvergivingTab({ focusCode }) {
  const assoc = useGet('/admin/associations');
  const queryClient = useQueryClient();
  const [current, setCurrent] = useState(focusCode || null);
  const code = current || assoc.data?.[0]?.code;
  // Pré-sélection depuis la Santé des données (« Configurer »).
  useEffect(() => { if (focusCode) setCurrent(focusCode); }, [focusCode]);
  const cfg = useGet(`/admin/evergiving/${code}`, undefined, !!code);
  const health = useGet('/sync/health', undefined, true);
  const [form, setForm] = useState({ campaign_id: '', schema_id: '' });
  const [syncing, setSyncing] = useState(false);
  const [liveJob, setLiveJob] = useState(null);
  const [pendingSource, setPendingSource] = useState(null);
  const [switchingSource, setSwitchingSource] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const pollRef = useRef(null);

  const d = cfg.data || {};
  const isConfigured = !!(d.campaign_id && d.schema_id);

  // Prefill inputs whenever the selected association config loads/changes.
  // `schema_id` par défaut 26075 (formulaire de bulletin partagé) si non configuré.
  useEffect(() => {
    setForm({
      campaign_id: d.campaign_id ?? '',
      schema_id: d.schema_id || '26075',
    });
    setLiveJob(null);
  }, [code, d.campaign_id, d.schema_id]);

  // Cleanup polling on unmount.
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const persist = async (patch, successMsg) => {
    try {
      await api.put(`/admin/evergiving/${code}`, patch);
      if (successMsg) toast.success(successMsg);
      cfg.refetch();
    } catch {
      toast.error("Échec de l'enregistrement");
    }
  };

  const saveIds = () => persist(
    { campaign_id: form.campaign_id, schema_id: form.schema_id },
    'Configuration enregistrée. La synchronisation live est prête.');

  // Lecture TEST (read-only) : valide un campaign_id et affiche l'ONG / code détectés,
  // sans rien écrire. Idéal pour retrouver le bon campaign_id d'une nouvelle association.
  const testIds = async () => {
    if (!String(form.campaign_id).trim()) { toast.error('Renseignez le campaign_id à tester'); return; }
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post('/sync/test', {
        campaign_id: form.campaign_id, schema_id: form.schema_id || 26075,
      });
      setTestResult(data);
      const ong = Object.keys(data.ongs || {})[0];
      toast.success(data.count ? `Détecté : ${ong || '—'} · ${data.count} bulletin(s)` : 'Connexion OK · 0 bulletin pour le moment');
    } catch (e) {
      setTestResult({ error: e?.response?.data?.detail || 'Échec du test' });
      toast.error(e?.response?.data?.detail || 'Échec du test');
    }
    setTesting(false);
  };

  // --- Détection automatique (scan borné des campaign_id Evergiving) ---
  const [discovering, setDiscovering] = useState(false);
  const [discovered, setDiscovered] = useState(null);
  const [adopting, setAdopting] = useState(null);

  const runDiscover = async () => {
    setDiscovering(true); setDiscovered(null);
    try {
      const { data } = await api.post('/sync/discover', { count: 400, budget_seconds: 50 });
      setDiscovered(data);
      const nb = (data.new || []).length;
      toast.success(nb ? `${nb} nouvelle(s) campagne(s) détectée(s).` : 'Aucune nouvelle campagne dans la fenêtre analysée.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Détection impossible');
    }
    setDiscovering(false);
  };

  const adopt = async (hit) => {
    const target = hit.suggested_association?.code || code;
    if (!target) { toast.error('Sélectionnez une association cible'); return; }
    setAdopting(hit.campaign_id);
    try {
      await api.post('/sync/adopt', { campaign_id: hit.campaign_id, schema_id: hit.schema_id, association_code: target });
      toast.success(`Campagne ${hit.campaign_id} adoptée → ${target}. Synchronisation lancée.`);
      setDiscovered((prev) => prev ? { ...prev, new: (prev.new || []).filter((h) => h.campaign_id !== hit.campaign_id) } : prev);
      cfg.refetch();
      setCurrent(target);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Adoption impossible");
    }
    setAdopting(null);
  };

  // --- Data source toggle (Excel <-> Evergiving live), reversible per association ---
  const activeSource = d.data_source === 'evergiving' ? 'evergiving' : 'excel';
  const excelCount = d.excel_record_count || 0;
  const liveCount = d.live_record_count || 0;
  const shortName = (assoc.data || []).find((a) => a.code === code)?.short_name || code;

  const confirmSwitchSource = async () => {
    if (!pendingSource) return;
    setSwitchingSource(true);
    try {
      await api.put(`/admin/evergiving/${code}`, { data_source: pendingSource });
      await cfg.refetch();
      // Invalidate all cached analytics so every view reflects the new source immediately.
      queryClient.invalidateQueries();
      toast.success(pendingSource === 'evergiving'
        ? `${shortName} pilote désormais sur la source Evergiving live.`
        : `${shortName} pilote désormais sur la source Excel (historique).`);
    } catch {
      toast.error('Échec de la bascule de source');
    } finally {
      setSwitchingSource(false);
      setPendingSource(null);
    }
  };

  const pollJob = () => {
    let ticks = 0;
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      ticks += 1;
      try {
        const { data } = await api.get('/sync/history', { params: { association: code } });
        const job = (data.jobs || []).find((j) => j.type === 'evergiving');
        if (job) setLiveJob(job);
        if (job && job.status !== 'running') {
          clearInterval(pollRef.current);
          setSyncing(false);
          cfg.refetch();
          if (job.status === 'success') {
            toast.success(`Synchronisation terminée : ${fmtInt(job.records_imported || 0)} nouveaux · ${fmtInt(job.records_updated || 0)} mis à jour`);
          } else if (job.status === 'awaiting_ids') {
            toast.warning('Configuration incomplète (campaign_id / schema_id).');
          } else if (job.status === 'error') {
            toast.error(`Échec : ${(job.errors || []).join(', ') || 'erreur inconnue'}`);
          }
        }
      } catch { /* keep polling */ }
      if (ticks >= 40) { // ~2 min guard; job continues in background
        clearInterval(pollRef.current);
        setSyncing(false);
        toast.info('Synchronisation toujours en cours — suivez son avancement dans « Logs ».');
      }
    }, 3000);
  };

  const runSync = async () => {
    try {
      setSyncing(true);
      const { data } = await api.post('/sync/run', { association: code });
      setLiveJob(data);
      if (data.status === 'awaiting_ids') {
        setSyncing(false);
        toast.warning('Renseignez campaign_id et schema_id avant de synchroniser.');
        return;
      }
      toast.info('Synchronisation lancée…');
      pollJob();
    } catch (e) {
      setSyncing(false);
      if (e?.response?.status === 409) toast.warning('Une synchronisation est déjà en cours.');
      else toast.error('Échec du lancement de la synchronisation');
    }
  };

  const h = health.data;
  const jobStatus = liveJob?.status;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
      {/* Source de données active (bascule réversible Excel <-> Evergiving live) */}
      <SectionCard
        title="Source de données active"
        subtitle="Choisissez la source qui alimente les tableaux de bord de cette association"
        className="lg:col-span-2"
        action={<Select value={code || ''} onValueChange={(v) => setCurrent(v)}>
          <SelectTrigger className="w-[170px]" data-testid="ev-source-assoc-select"><SelectValue /></SelectTrigger>
          <SelectContent>{(assoc.data || []).map((a) => <SelectItem key={a.code} value={a.code}>{a.short_name}</SelectItem>)}</SelectContent>
        </Select>}>
        {cfg.isLoading ? <CardSkeleton height="h-32" /> : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2" data-testid="data-source-panel">
            {/* Tuile Excel */}
            <button
              type="button"
              onClick={() => { if (activeSource !== 'excel') setPendingSource('excel'); }}
              disabled={switchingSource}
              data-testid="source-tile-excel"
              className={`group flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${activeSource === 'excel' ? 'accent-border accent-bg-soft ring-2 ring-[var(--tenant-accent-500)]' : 'bg-card hover:bg-muted/50'} ${activeSource === 'excel' ? 'cursor-default' : 'cursor-pointer'}`}>
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${activeSource === 'excel' ? 'accent-bg-100' : 'bg-muted'}`}>
                <FileSpreadsheet className={`h-5 w-5 ${activeSource === 'excel' ? 'accent-text' : 'text-muted-foreground'}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-display text-sm font-semibold">Excel (historique)</p>
                  {activeSource === 'excel' && <Badge className="rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" data-testid="source-active-excel">Active</Badge>}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground tabular">{fmtInt(excelCount)} enregistrement(s) · inclut les heures terrain</p>
              </div>
            </button>

            {/* Tuile Evergiving live */}
            <button
              type="button"
              onClick={() => { if (activeSource !== 'evergiving' && liveCount > 0) setPendingSource('evergiving'); }}
              disabled={switchingSource || (activeSource !== 'evergiving' && liveCount === 0)}
              title={liveCount === 0 ? 'Lancez d\u2019abord une synchronisation Evergiving réussie pour activer cette source.' : undefined}
              data-testid="source-tile-live"
              className={`group flex items-center gap-3 rounded-2xl border p-4 text-left transition-colors ${activeSource === 'evergiving' ? 'accent-border accent-bg-soft ring-2 ring-[var(--tenant-accent-500)]' : 'bg-card hover:bg-muted/50'} ${activeSource === 'evergiving' ? 'cursor-default' : liveCount === 0 ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${activeSource === 'evergiving' ? 'accent-bg-100' : 'bg-muted'}`}>
                <Wifi className={`h-5 w-5 ${activeSource === 'evergiving' ? 'accent-text' : 'text-muted-foreground'}`} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="font-display text-sm font-semibold">Evergiving live</p>
                  {activeSource === 'evergiving' && <Badge className="rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" data-testid="source-active-live">Active</Badge>}
                  {liveCount === 0 && <Badge variant="outline" className="rounded-full bg-amber-50 text-amber-800 ring-1 ring-amber-200">Aucune synchro</Badge>}
                </div>
                <p className="mt-0.5 text-xs text-muted-foreground tabular">
                  {liveCount === 0 ? 'Aucune donnée synchronisée · lancez une synchro' : `${fmtInt(liveCount)} enregistrement(s) synchronisé(s)`}
                </p>
              </div>
            </button>
          </div>
        )}
        <div className="mt-3 flex items-center gap-2 rounded-xl border bg-muted/40 p-2.5 text-xs text-muted-foreground" data-testid="source-current-note">
          <ArrowLeftRight className="h-4 w-4 shrink-0 accent-text" />
          <span>Bascule <strong>réversible</strong> à tout moment. Source active : <strong className="text-foreground">{activeSource === 'evergiving' ? 'Evergiving live' : 'Excel (historique)'}</strong> pour <strong className="text-foreground">{shortName}</strong>.</span>
        </div>
      </SectionCard>

      {/* Connexion & état de synchro */}
      <SectionCard title="Connexion & synchronisation" subtitle="État de l'API Evergiving et dernière synchro">
        <div className="flex items-center gap-3">
          <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${h?.ok ? 'bg-emerald-50' : 'bg-rose-50'}`}>
            <Activity className={`h-5 w-5 ${h?.ok ? 'text-emerald-600' : 'text-rose-600'}`} />
          </div>
          <div>
            <p className="font-display text-sm font-semibold">{h?.ok ? 'API opérationnelle' : (h?.state || 'Vérification…')}</p>
            <p className="text-xs text-muted-foreground">{h?.note}</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border bg-card p-3" data-testid="ev-last-sync">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Clock className="h-3.5 w-3.5" /> Dernière synchro</p>
            <p className="mt-1 text-sm font-medium">{d.last_synced_at ? fmtDateTime(d.last_synced_at) : 'Jamais'}</p>
          </div>
          <div className="rounded-xl border bg-card p-3" data-testid="ev-last-imported">
            <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Database className="h-3.5 w-3.5" /> Derniers imports</p>
            <p className="mt-1 text-sm font-medium tabular">{d.last_synced_at ? `${fmtInt(d.last_records_imported || 0)} + ${fmtInt(d.last_records_updated || 0)} maj` : '—'}</p>
          </div>
        </div>

        {jobStatus && (
          <div className="mt-3 flex items-center gap-2 rounded-xl border bg-muted/40 p-2.5 text-xs" data-testid="ev-job-status">
            {jobStatus === 'running' && <RefreshCw className="h-4 w-4 animate-spin accent-text" />}
            {jobStatus === 'success' && <CheckCircle2 className="h-4 w-4 text-emerald-600" />}
            {(jobStatus === 'error' || jobStatus === 'awaiting_ids') && <AlertTriangle className="h-4 w-4 text-amber-600" />}
            <span className="font-medium">
              {jobStatus === 'running' ? 'Synchronisation en cours…'
                : jobStatus === 'success' ? `Terminée · ${fmtInt(liveJob.records_imported || 0)} nouveaux`
                : jobStatus === 'awaiting_ids' ? 'En attente de configuration'
                : 'Erreur de synchronisation'}
            </span>
          </div>
        )}

        <Button
          className="mt-4 w-full gap-2 text-white"
          style={{ background: 'var(--tenant-accent-500)' }}
          onClick={runSync}
          disabled={syncing || !isConfigured}
          data-testid="run-sync-btn">
          <RefreshCw className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Synchronisation…' : 'Lancer une synchronisation'}
        </Button>
        {!isConfigured && <p className="mt-2 text-center text-xs text-amber-600">Configurez campaign_id et schema_id pour activer la synchronisation.</p>}
      </SectionCard>

      {/* Configuration IDs */}
      <SectionCard title="Configuration par association"
        action={<Select value={code || ''} onValueChange={(v) => setCurrent(v)}>
          <SelectTrigger className="w-[170px]" data-testid="ev-assoc-select"><SelectValue /></SelectTrigger>
          <SelectContent>{(assoc.data || []).map((a) => <SelectItem key={a.code} value={a.code}>{a.short_name}</SelectItem>)}</SelectContent>
        </Select>}>
        {cfg.isLoading ? <CardSkeleton height="h-40" /> : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={`rounded-full ring-1 ${isConfigured ? 'bg-emerald-50 text-emerald-700 ring-emerald-200' : 'bg-amber-50 text-amber-800 ring-amber-200'}`}>
                {isConfigured ? 'Configuré' : 'Non configuré'}
              </Badge>
              {d.cursor && <Badge variant="secondary" className="rounded-full text-[10px]">Synchro incrémentale active</Badge>}
            </div>
            <Field label="campaign_id"><Input value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })} placeholder="ex: 5163" data-testid="ev-campaign-input" /></Field>
            <Field label="schema_id"><Input value={form.schema_id} onChange={(e) => setForm({ ...form, schema_id: e.target.value })} placeholder="ex: 26075" data-testid="ev-schema-input" /></Field>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={testIds} disabled={testing} className="gap-2" data-testid="ev-test-btn">
                {testing ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />} Tester la connexion
              </Button>
              <Button onClick={saveIds} className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="save-evergiving-btn"><Save className="h-4 w-4" /> Enregistrer</Button>
            </div>
            {testResult && !testResult.error && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs dark:border-emerald-900 dark:bg-emerald-950/40" data-testid="ev-test-result">
                <p className="flex items-center gap-1.5 font-semibold text-emerald-800 dark:text-emerald-300"><CheckCircle2 className="h-4 w-4" /> Connexion réussie · {fmtInt(testResult.count)} bulletin(s) dans le lot</p>
                <div className="mt-1.5 space-y-0.5 text-emerald-900/90 dark:text-emerald-200/90">
                  <p>ONG détectée : <b>{Object.keys(testResult.ongs || {}).join(', ') || '—'}</b></p>
                  <p>Code(s) campagne : <b className="font-mono">{Object.keys(testResult.codes || {}).join(', ') || '—'}</b></p>
                </div>
                <p className="mt-1.5 text-[11px] text-emerald-700/80 dark:text-emerald-300/70">Vérifiez que l'ONG correspond, puis « Enregistrer » et lancez une synchronisation.</p>
              </div>
            )}
            {testResult?.error && (
              <div className="rounded-xl border border-rose-200 bg-rose-50 p-2.5 text-xs text-rose-800 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300" data-testid="ev-test-error">
                <p className="flex items-center gap-1.5 font-medium"><AlertTriangle className="h-4 w-4" /> {testResult.error}</p>
              </div>
            )}
            <p className="text-xs text-muted-foreground">Le <b>schema_id</b> est généralement <b className="font-mono">26075</b> (formulaire de bulletin partagé). Testez un <b>campaign_id</b> pour confirmer l'association avant d'enregistrer.</p>
          </div>
        )}
      </SectionCard>

      {/* Détection automatique des campagnes Evergiving */}
      <SectionCard title="Détection automatique" className="lg:col-span-2"
        subtitle="Repère les campagnes Evergiving non encore branchées, sans rien saisir"
        action={
          <Button onClick={runDiscover} disabled={discovering} className="gap-2 text-white"
            style={{ background: 'var(--tenant-accent-500)' }} data-testid="ev-discover-btn">
            {discovering ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            {discovering ? 'Analyse en cours…' : 'Lancer la détection'}
          </Button>}>
        {!discovered && !discovering && (
          <p className="text-sm text-muted-foreground">
            Cliquez sur <b>Lancer la détection</b> : la plateforme scanne les campagnes Evergiving et vous
            propose d'<b>adopter en un clic</b> celles qui ne sont pas encore reliées à une association.
          </p>
        )}
        {discovering && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><RefreshCw className="h-4 w-4 animate-spin accent-text" /> Analyse des campagnes Evergiving… (quelques secondes)</div>
        )}
        {discovered && (
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Fenêtre analysée : campagnes <b>{discovered.scanned_from}</b> → <b>{discovered.scanned_to}</b> · {discovered.elapsed_s}s
              {discovered.budget_reached ? ' (analyse écourtée — relancez pour élargir)' : ''}
            </p>
            {(discovered.new || []).length === 0 ? (
              <div className="flex items-center gap-2 rounded-xl border bg-muted/40 p-3 text-sm text-muted-foreground" data-testid="ev-discover-empty">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" /> Aucune nouvelle campagne à adopter dans cette fenêtre.
              </div>
            ) : (
              <div className="space-y-2" data-testid="ev-discover-results">
                {(discovered.new || []).map((hit) => (
                  <div key={hit.campaign_id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border p-3" data-testid={`ev-discover-hit-${hit.campaign_id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold">{hit.ong || 'ONG inconnue'} <span className="ml-1 font-mono text-xs text-muted-foreground">#{hit.campaign_id}</span></p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Code <b className="font-mono">{hit.code_campagne || '—'}</b> · {fmtInt(hit.count)} bulletin(s) ·
                        {hit.suggested_association
                          ? <> association proposée : <b>{hit.suggested_association.name}</b></>
                          : <> aucune association correspondante — sélectionnez-la d'abord ci-dessus</>}
                      </p>
                    </div>
                    <Button size="sm" onClick={() => adopt(hit)} disabled={adopting === hit.campaign_id || (!hit.suggested_association && !code)}
                      className="gap-1.5 text-white" style={{ background: 'var(--tenant-accent-500)' }}
                      data-testid={`ev-adopt-${hit.campaign_id}`}>
                      {adopting === hit.campaign_id ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
                      Adopter → {hit.suggested_association?.code || code || '?'}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </SectionCard>
      <SectionCard title="Synchronisation automatique" subtitle="Planification en arrière-plan">
        <div className="flex items-center justify-between rounded-xl border bg-card p-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl accent-bg-100"><Zap className="h-5 w-5 accent-text" /></div>
            <div>
              <p className="text-sm font-medium">Synchro automatique</p>
              <p className="text-xs text-muted-foreground">{d.auto_sync_enabled ? 'Activée' : 'Désactivée'}</p>
            </div>
          </div>
          <Switch
            checked={!!d.auto_sync_enabled}
            disabled={!isConfigured}
            onCheckedChange={(v) => persist({ auto_sync_enabled: v }, v ? 'Synchro automatique activée' : 'Synchro automatique désactivée')}
            data-testid="ev-auto-switch" />
        </div>
        <div className="mt-3">
          <Field label="Fréquence">
            <Select
              value={String(d.auto_sync_interval_minutes || 60)}
              onValueChange={(v) => persist({ auto_sync_interval_minutes: Number(v) }, 'Fréquence mise à jour')}
              disabled={!d.auto_sync_enabled}>
              <SelectTrigger data-testid="ev-interval-select"><SelectValue /></SelectTrigger>
              <SelectContent>{INTERVAL_OPTIONS.map((o) => <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <p className="mt-2 text-xs text-muted-foreground">La synchronisation incrémentale récupère uniquement les nouveaux bulletins et les mises à jour de statut depuis la dernière exécution.</p>
        </div>
      </SectionCard>

      {/* Note produit : limitation heures/productivité */}
      <SectionCard title="À savoir">
        <div className="flex items-start gap-3 rounded-xl border bg-amber-50/60 p-3 ring-1 ring-amber-200">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="space-y-1 text-sm">
            <p className="font-medium text-amber-900">Heures terrain non fournies par Evergiving</p>
            <p className="text-xs text-amber-800">
              L'API Evergiving ne transmet pas les <strong>heures travaillées</strong>. Les indicateurs de <strong>productivité (BS/heure)</strong> ne peuvent donc pas être calculés à partir de la synchro live seule. Les données historiques Excel restent la source par défaut des tableaux de bord tant que la bascule (P2) n'est pas activée.
            </p>
          </div>
        </div>
      </SectionCard>

      {/* Confirmation de bascule de source */}
      <AlertDialog open={!!pendingSource} onOpenChange={(o) => { if (!o) setPendingSource(null); }}>
        <AlertDialogContent data-testid="source-switch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingSource === 'evergiving' ? 'Activer la source Evergiving live ?' : 'Revenir à la source Excel ?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingSource === 'evergiving' ? (
                <>
                  Les tableaux de bord de <strong>{shortName}</strong> seront alimentés par les
                  {' '}<strong>{fmtInt(liveCount)}</strong> enregistrement(s) synchronisés depuis Evergiving.
                  {liveCount === 0 && (
                    <> {' '}<strong className="text-amber-700">Attention : aucune donnée n'a encore été synchronisée</strong> —
                    {' '}les tableaux de bord resteront vides jusqu'à la première synchronisation.</>
                  )}
                  {' '}Rappel : l'API ne fournit pas les <strong>heures terrain</strong>, la
                  {' '}<strong>productivité (BS/heure)</strong> ne sera donc pas calculable en mode live.
                  {' '}La bascule reste <strong>réversible</strong> à tout moment.
                </>
              ) : (
                <>
                  Les tableaux de bord de <strong>{shortName}</strong> repasseront sur les
                  {' '}<strong>{fmtInt(excelCount)}</strong> enregistrement(s) Excel historiques
                  {' '}(incluant les heures terrain et la productivité). La bascule reste <strong>réversible</strong>.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="source-switch-cancel" disabled={switchingSource}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmSwitchSource(); }}
              disabled={switchingSource}
              className="text-white"
              style={{ background: 'var(--tenant-accent-500)' }}
              data-testid="source-switch-confirm">
              {switchingSource ? 'Bascule…' : 'Confirmer la bascule'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function LogsTab() {
  const { data, isLoading } = useGet('/admin/logs');
  if (isLoading) return <CardSkeleton />;
  return (
    <SectionCard title="Journal système" subtitle="Toutes les synchronisations et imports" bodyClassName="!px-0">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent"><TableHead>Association</TableHead><TableHead>Type</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Enreg.</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
          <TableBody>
            {(data || []).map((j) => (
              <TableRow key={j.id} data-testid="admin-log-row">
                <TableCell className="text-sm">{j.association_code}</TableCell>
                <TableCell className="text-xs">{j.type}</TableCell>
                <TableCell><Badge variant="secondary" className="rounded-full">{j.status}</Badge></TableCell>
                <TableCell className="text-right tabular">{fmtInt(j.records_imported || 0)}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDateTime(j.started_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </SectionCard>
  );
}

const WEEKDAYS_FR = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const pad2 = (n) => String(n).padStart(2, '0');
const toISO = (d) => `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;

function currentISOWeek() {
  const now = new Date();
  const d = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { year: d.getUTCFullYear(), week };
}

function isoWeekDates(year, week) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const day = jan4.getUTCDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setUTCDate(jan4.getUTCDate() - day + 1);
  const monday = new Date(week1Monday);
  monday.setUTCDate(week1Monday.getUTCDate() + (week - 1) * 7);
  return WEEKDAYS_FR.map((label, i) => {
    const dt = new Date(monday);
    dt.setUTCDate(monday.getUTCDate() + i);
    return { label, iso: toISO(dt), display: `${pad2(dt.getUTCDate())}/${pad2(dt.getUTCMonth() + 1)}` };
  });
}

// Sélecteur de canal INLINE sur une carte de saisie : remplace le badge figé (y
// compris « Non renseigné ») par une liste déroulante des canaux existants. La
// sélection crée/actualise l'override du code campagne → propagé automatiquement
// partout (matrice, comparaison, objectifs, exports…).
function CanalInlinePicker({ code, assocCode, current, options, onDone }) {
  const [saving, setSaving] = useState(false);
  const isSet = current && current !== 'Non renseigné';
  const save = async (v) => {
    if (!v || v === current) return;
    setSaving(true);
    try {
      await api.put(`/admin/campaign-codes/${encodeURIComponent(code)}`, { canal: v, association_code: assocCode });
      toast.success(`Canal « ${v} » appliqué à ${code} — mis à jour partout`);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Mise à jour impossible');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Select value={isSet ? current : undefined} onValueChange={save} disabled={saving}>
      <SelectTrigger
        className={`h-6 w-auto shrink-0 gap-1 rounded-full border px-2 py-0 text-xs font-medium focus:ring-2 ${isSet
          ? 'border-transparent bg-secondary text-secondary-foreground hover:bg-secondary/80'
          : 'border-amber-400 bg-amber-50 text-amber-700 hover:bg-amber-100 dark:border-amber-500/60 dark:bg-amber-950/30 dark:text-amber-300'}`}
        data-testid={`manual-canal-picker-${code}`}
        title={isSet ? 'Changer le canal de ce code' : 'Canal non renseigné — cliquez pour le définir'}
      >
        {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <SelectValue placeholder="Définir le canal" />}
      </SelectTrigger>
      <SelectContent>
        {(options || []).map((o) => <SelectItem key={o} value={o} className="text-xs">{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}


function ManualHoursCard({ associations, onSaved }) {
  const qc = useQueryClient();
  const { current: tenantAssoc } = useTenant();   // association sélectionnée en haut (jamais ALL)
  const lastTenant = useRef(null);
  const cur = useMemo(() => currentISOWeek(), []);
  const [assocCode, setAssocCode] = useState('');
  const [year, setYear] = useState(cur.year);
  const [week, setWeek] = useState(cur.week);
  const [hours, setHours] = useState({});            // {code: {iso: string}}
  const [initialTotals, setInitialTotals] = useState({});
  const [extra, setExtra] = useState([]);            // manually-added codes not auto-detected
  const [addOpen, setAddOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  // L'association de saisie suit AUTOMATIQUEMENT le sélecteur global (en haut) :
  // on ne redemande plus de rechoisir l'association. Le choix local reste possible
  // (override) jusqu'au prochain changement du sélecteur global.
  useEffect(() => {
    if (tenantAssoc && tenantAssoc !== lastTenant.current) {
      lastTenant.current = tenantAssoc;
      setAssocCode(tenantAssoc);
    } else if (!assocCode && associations?.length) {
      setAssocCode(associations[0].code);
    }
  }, [tenantAssoc, associations, assocCode]);

  const days = useMemo(() => isoWeekDates(Number(year), Number(week)), [year, week]);

  const wcQ = useGet('/admin/hr/week-codes', { association: assocCode, year, week }, !!assocCode);
  const detected = useMemo(() => wcQ.data?.codes || [], [wcQ.data]);
  const allCodes = useGet('/admin/hr/codes', { association: assocCode }, !!assocCode).data?.codes || [];
  const canalOptions = useGet('/admin/campaign-codes').data?.canal_options || CANAL_FALLBACK;

  const displayCodes = useMemo(() => {
    const map = new Map();
    detected.forEach((c) => map.set(c.code, c));
    extra.forEach((code) => {
      if (!map.has(code)) {
        const info = allCodes.find((x) => x.code === code) || {};
        map.set(code, { code, label: info.label || code, canal: info.canal, region: info.region, bs_rue: 0, bs_reel: 0, active_days: 0, days: {}, hours: {}, _manual: true });
      }
    });
    return Array.from(map.values());
  }, [detected, extra, allCodes]);

  // Combien de codes AFFICHÉS partagent la même base (code sans lettre de séquence).
  // > 1 => on affiche un badge « séquence X » pour lever la confusion des doublons.
  const baseCount = useMemo(() => {
    const m = {};
    displayCodes.forEach((c) => { const b = seqInfo(c.code).base; m[b] = (m[b] || 0) + 1; });
    return m;
  }, [displayCodes]);

  // Seed hour inputs from detected/existing values whenever the week context changes.
  useEffect(() => {
    const h = {};
    const tot = {};
    detected.forEach((c) => {
      h[c.code] = {};
      let t = 0;
      Object.keys(c.hours || {}).forEach((iso) => { h[c.code][iso] = String(c.hours[iso]); t += c.hours[iso] || 0; });
      tot[c.code] = t;
    });
    setHours(h);
    setInitialTotals(tot);
    setExtra([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assocCode, year, week, wcQ.dataUpdatedAt]);

  const setDay = (code, iso, val) => setHours((prev) => ({ ...prev, [code]: { ...(prev[code] || {}), [iso]: val } }));
  const codeTotal = (code) => days.reduce((s, d) => s + (parseFloat(hours[code]?.[d.iso]) || 0), 0);
  const grandTotal = displayCodes.reduce((s, c) => s + codeTotal(c.code), 0);
  const grandBS = displayCodes.reduce((s, c) => s + (c.bs_reel || 0), 0);

  const goCurrentWeek = () => { setYear(cur.year); setWeek(cur.week); };
  const shiftWeek = (delta) => {
    let w = Number(week) + delta;
    let y = Number(year);
    if (w < 1) { y -= 1; w = 52; } else if (w > 53) { y += 1; w = 1; }
    setWeek(w); setYear(y);
  };

  const save = async () => {
    if (!assocCode) { toast.error('Choisissez une association.'); return; }
    const batch = [];
    displayCodes.forEach((c) => {
      const entries = days.map((d) => ({ date: d.iso, heures: parseFloat(hours[c.code]?.[d.iso]) || 0 }));
      const total = entries.reduce((s, e) => s + e.heures, 0);
      const had = (initialTotals[c.code] || 0) > 0;
      if (total > 0 || had) batch.push({ code: c.code, entries });
    });
    if (!batch.length) { toast.error('Saisissez au moins une heure.'); return; }
    setSaving(true);
    try {
      let sum = 0;
      for (const b of batch) {
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.post('/admin/hr/manual', { association_code: assocCode, campaign_code: b.code, entries: b.entries });
        sum += data.total_heures || 0;
      }
      toast.success(`${batch.length} code(s) · S${week} ${year} : ${fmtInt(Math.round(sum))} h enregistrées`);
      qc.invalidateQueries();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const isCurrentWeek = Number(year) === cur.year && Number(week) === cur.week;
  const addableCodes = allCodes.filter((c) => !displayCodes.some((d) => d.code === c.code));

  return (
    <SectionCard
      title="Saisir les heures manuellement"
      subtitle="Les codes campagne actifs de la semaine sont détectés automatiquement (comme votre fichier). Il ne reste qu'à saisir les heures — prise en compte immédiate partout."
      testid="hr-manual-card"
    >
      <div className="flex flex-wrap items-end gap-4">
        <Field label="Association">
          <Select value={assocCode} onValueChange={setAssocCode}>
            <SelectTrigger className="w-48" data-testid="manual-assoc-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
            <SelectContent>
              {(associations || []).map((a) => <SelectItem key={a.code} value={a.code}>{a.short_name || a.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <p className="mt-1 text-xs text-muted-foreground" data-testid="manual-assoc-hint">Suit l'association choisie en haut — modifiable ici si besoin.</p>
        </Field>
        <Field label="Semaine (ISO)">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => shiftWeek(-1)} data-testid="manual-week-prev">‹</Button>
            <Input type="number" min="1" max="53" value={week} onChange={(e) => setWeek(e.target.value)} className="w-16 text-center" data-testid="manual-week-input" />
            <Input type="number" value={year} onChange={(e) => setYear(e.target.value)} className="w-24 text-center" data-testid="manual-year-input" />
            <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" onClick={() => shiftWeek(1)} data-testid="manual-week-next">›</Button>
          </div>
        </Field>
        <div className="flex items-center gap-2 pb-1">
          <Badge variant="secondary" className="gap-1 rounded-full" data-testid="manual-week-range">
            <CalendarClock className="h-3.5 w-3.5" />
            Semaine {week} · du {days[0]?.display} au {days[6]?.display} {year}
          </Badge>
          {!isCurrentWeek && (
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={goCurrentWeek} data-testid="manual-current-week">
              Semaine en cours
            </Button>
          )}
        </div>
      </div>

      {/* Auto-detected campaign code blocks */}
      <div className="mt-4 space-y-3">
        {wcQ.isLoading ? (
          <div className="h-40 animate-pulse rounded-2xl bg-muted/50" />
        ) : displayCodes.length === 0 ? (
          <div className="rounded-2xl border border-dashed bg-muted/30 p-4 text-sm text-muted-foreground" data-testid="manual-no-codes">
            Aucun code campagne détecté pour cette semaine. Utilisez « Ajouter un code » ci-dessous pour saisir malgré tout.
          </div>
        ) : (
          displayCodes.map((c) => (
            <div key={c.code} className="rounded-2xl border bg-muted/20 p-3" data-testid="manual-code-block">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex min-w-0 flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-bold accent-text" data-testid="manual-block-code">{c.code}</span>
                  <span className="break-words text-sm text-muted-foreground">{c.label}</span>
                  {(() => {
                    const { seq, base } = seqInfo(c.code);
                    return seq && baseCount[base] > 1 ? (
                      <Badge variant="outline" className="shrink-0 border-current accent-text" data-testid={`manual-seq-badge-${c.code}`} title={`Même canal que ${base}… — séquence ${seq}`}>
                        séquence {seq}
                      </Badge>
                    ) : null;
                  })()}
                  {c._manual && <Badge variant="outline" className="shrink-0">ajouté</Badge>}
                  <CanalInlinePicker
                    code={c.code}
                    assocCode={assocCode}
                    current={c.canal}
                    options={canalOptions}
                    onDone={() => qc.invalidateQueries()}
                  />
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {c._manual ? 'aucune activité détectée' : `${fmtInt(c.bs_reel)} BS validés · ${c.active_days || 0} j actifs`}
                </span>
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-7">
                {days.map((d) => {
                  const bs = c.days?.[d.iso];
                  const activeDay = bs && (bs.bs_reel || bs.bs_rue);
                  const dayBs = bs?.bs_reel || 0;
                  const dayHours = parseFloat(hours[c.code]?.[d.iso]) || 0;
                  const dayRate = dayHours > 0 ? dayBs / dayHours : null;
                  return (
                    <div key={d.iso} className={`rounded-xl border p-2 ${activeDay ? 'bg-card' : 'bg-muted/30'}`} data-testid="manual-day-cell">
                      <p className="text-xs font-semibold text-foreground">{d.label}</p>
                      <p className="text-[10px] text-muted-foreground">{d.display}{bs?.bs_reel ? ` · ${fmtInt(bs.bs_reel)} BS` : ''}</p>
                      <div className="mt-1 flex items-center gap-1">
                        <Input type="number" min="0" step="0.5" inputMode="decimal"
                          value={hours[c.code]?.[d.iso] ?? ''} onChange={(e) => setDay(c.code, d.iso, e.target.value)}
                          placeholder="0" className="h-9 tabular" data-testid={`manual-hours-${c.code}-${d.label}`} />
                        <span className="text-xs text-muted-foreground">h</span>
                      </div>
                      <p className="mt-1 h-3.5 text-[10px] leading-none text-muted-foreground" data-testid={`manual-day-rate-${c.code}-${d.label}`}>
                        {dayRate !== null && <>Taux réel : <strong className="tabular accent-text">{dayRate.toFixed(2)}</strong></>}
                      </p>
                    </div>
                  );
                })}
              </div>
              <div className="mt-1.5 flex flex-wrap items-center justify-end gap-x-5 gap-y-1 text-xs text-muted-foreground">
                <span data-testid={`manual-code-taux-${c.code}`}>
                  Taux réel : <strong className="tabular accent-text">{codeTotal(c.code) > 0 ? (c.bs_reel / codeTotal(c.code)).toFixed(2) : '—'}</strong>
                  <span className="ml-1 text-[11px]">({fmtInt(c.bs_reel)} BS ÷ {fmtInt(Math.round(codeTotal(c.code) * 10) / 10)} h)</span>
                </span>
                <span>
                  Total : <strong className="text-foreground tabular" data-testid={`manual-code-total-${c.code}`}>{fmtInt(Math.round(codeTotal(c.code) * 10) / 10)} h</strong>
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer: add-a-code fallback + grand total + save */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Popover open={addOpen} onOpenChange={setAddOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" disabled={!assocCode} data-testid="manual-addcode-trigger">
                <Plus className="h-4 w-4" /> Ajouter un code
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 p-0" align="start">
              <Command>
                <CommandInput placeholder="Rechercher un code…" data-testid="manual-addcode-search" />
                <CommandList>
                  <CommandEmpty>Aucun code.</CommandEmpty>
                  <CommandGroup>
                    {addableCodes.map((c) => (
                      <CommandItem key={c.code} value={`${c.code} ${c.label} ${c.canal}`}
                        onSelect={() => { setExtra((x) => [...x, c.code]); setAddOpen(false); }} data-testid="manual-addcode-option">
                        <span className="flex min-w-0 flex-col">
                          <span className="font-mono text-xs font-semibold">{c.code}</span>
                          <span className="truncate text-[11px] text-muted-foreground">{c.label} · {c.canal}</span>
                        </span>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Taux réel :</span>
            <span className="font-display text-lg font-bold tabular accent-text" data-testid="manual-taux-global">{grandTotal > 0 ? (grandBS / grandTotal).toFixed(2) : '—'}</span>
            <span className="text-[11px] text-muted-foreground">({fmtInt(grandBS)} BS ÷ {fmtInt(Math.round(grandTotal * 10) / 10)} h)</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Total semaine :</span>
            <span className="font-display text-lg font-bold tabular text-foreground" data-testid="manual-total">{fmtInt(Math.round(grandTotal * 10) / 10)} h</span>
          </div>
        </div>
        <Button onClick={save} disabled={saving || !displayCodes.length} className="gap-2 rounded-xl text-white"
          style={{ background: 'var(--tenant-accent-500)' }} data-testid="manual-save-btn">
          <Save className="h-4 w-4" />
          {saving ? 'Enregistrement…' : 'Enregistrer les heures'}
        </Button>
      </div>
    </SectionCard>
  );
}


function HRHoursTab() {
  const assoc = useGet('/admin/associations');
  const { data, isLoading, refetch } = useGet('/admin/hr/summary');
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [assocCode, setAssocCode] = useState('auto');
  const [year, setYear] = useState('');
  const [uploading, setUploading] = useState(false);
  const [report, setReport] = useState(null);
  const [toDelete, setToDelete] = useState(null);

  const imports = data?.imports || [];
  const totalHeures = imports.reduce((s, i) => s + (i.heures || 0), 0);

  const doUpload = async () => {
    if (!file) { toast.error('Sélectionnez un fichier reporting (.xlsx).'); return; }
    setUploading(true);
    setReport(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (assocCode && assocCode !== 'auto') fd.append('association_code', assocCode);
      if (year.trim()) fd.append('year', year.trim());
      const { data: rep } = await api.post('/admin/hr/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setReport(rep);
      toast.success(`${rep.association} ${rep.year} : ${fmtInt(Math.round(rep.total_heures))} h importées`);
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      refetch();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Import impossible. Vérifiez le fichier.";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const doDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/admin/hr/${toDelete.association_code}/${toDelete.year}`);
      toast.success('Import supprimé');
      setToDelete(null);
      refetch();
    } catch {
      toast.error('Suppression impossible');
    }
  };

  return (
    <div className="space-y-6" data-testid="hr-tab">
      {/* Explanation */}
      <div className="flex items-start gap-3 rounded-2xl border bg-muted/40 p-4" data-testid="hr-intro">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl accent-bg-soft">
          <Timer className="h-5 w-5 accent-text" />
        </div>
        <div className="text-sm text-muted-foreground">
          <p className="font-display font-semibold text-foreground">Import des heures travaillées (reportings SHARITI)</p>
          <p className="mt-0.5">
            Evergiving ne fournit pas les heures : cet import restaure la <strong>Productivité (BS validés / heure)</strong> en
            mode live. Les heures sont rattachées par <strong>code campagne + date</strong> (résolus via la taxonomie).
            Les BS, montants et âges ne sont jamais modifiés.
          </p>
        </div>
      </div>

      {/* Upload */}
      <SectionCard title="Importer un fichier reporting" subtitle="Format .xlsx — association et année détectées automatiquement depuis le nom du fichier">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <div className="md:col-span-2">
            <Field label="Fichier reporting (.xlsx)">
              <Input ref={fileRef} type="file" accept=".xlsx,.xlsm"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="hr-file-input" />
            </Field>
          </div>
          <Field label="Association (optionnel)">
            <Select value={assocCode} onValueChange={setAssocCode}>
              <SelectTrigger data-testid="hr-assoc-select"><SelectValue placeholder="Auto" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Détection auto</SelectItem>
                {(assoc.data || []).map((a) => <SelectItem key={a.code} value={a.code}>{a.short_name || a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Année (optionnel)">
            <Input value={year} onChange={(e) => setYear(e.target.value)} placeholder="Auto (ex: 2025)" data-testid="hr-year-input" />
          </Field>
        </div>
        <div className="mt-4 flex items-center gap-3">
          <Button onClick={doUpload} disabled={uploading || !file} className="gap-2 rounded-xl text-white"
            style={{ background: 'var(--tenant-accent-500)' }} data-testid="hr-upload-btn">
            <Upload className="h-4 w-4" />
            {uploading ? 'Import en cours…' : 'Importer les heures'}
          </Button>
          {file && <span className="break-all text-xs text-muted-foreground">{file.name}</span>}
        </div>

        {report && (
          <div className="mt-4 rounded-xl border bg-card p-4" data-testid="hr-report">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <span className="font-display font-semibold">{report.association} · {report.year}</span>
              <Badge variant="secondary" className="rounded-full">{fmtInt(Math.round(report.total_heures))} h</Badge>
              <Badge variant="secondary" className="rounded-full">{report.total_days} jours</Badge>
              <Badge variant="secondary" className="rounded-full">{report.inserted} lignes</Badge>
            </div>
            {report.sheets?.length > 0 && (
              <div className="mt-3 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow className="hover:bg-transparent">
                    <TableHead>Onglet</TableHead><TableHead>Code</TableHead><TableHead>Canal</TableHead>
                    <TableHead>Région</TableHead><TableHead className="text-right">Heures</TableHead><TableHead>Résolution</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {report.sheets.filter((s) => s.heures > 0).map((s) => (
                      <TableRow key={s.sheet} data-testid="hr-report-row">
                        <TableCell className="text-xs">{s.sheet}</TableCell>
                        <TableCell className="font-mono text-xs font-semibold accent-text">{s.code || '—'}</TableCell>
                        <TableCell className="text-xs">{s.canal || '—'}</TableCell>
                        <TableCell className="text-xs">{s.region || '—'}</TableCell>
                        <TableCell className="text-right tabular text-xs">{fmtInt(Math.round(s.heures))}</TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="rounded-full text-[10px]">
                            {s.method === 'code' ? 'Code' : s.method === 'taxonomy' ? 'Taxonomie' : '—'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            {report.unresolved_sheets?.length > 0 && (
              <div className="mt-2 flex items-center gap-2 text-xs text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" />
                Onglets non résolus (ignorés) : {report.unresolved_sheets.join(', ')}
              </div>
            )}
          </div>
        )}
      </SectionCard>

      {/* Manual weekly hours entry */}
      <ManualHoursCard associations={assoc.data || []} onSaved={refetch} />

      {/* Existing imports */}
      <SectionCard
        title="Imports enregistrés"
        subtitle={`${imports.length} import(s) · ${fmtInt(Math.round(totalHeures))} heures au total`}
        bodyClassName="!px-0">
        {isLoading ? <div className="px-6"><CardSkeleton /></div> : imports.length === 0 ? (
          <p className="px-6 py-8 text-center text-sm text-muted-foreground" data-testid="hr-empty">
            Aucune heure importée pour le moment. Importez un fichier reporting ci-dessus.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="hr-imports-table">
              <TableHeader><TableRow className="hover:bg-transparent">
                <TableHead>Association</TableHead><TableHead>Année</TableHead>
                <TableHead className="text-right">Heures</TableHead><TableHead className="text-right">Jours</TableHead>
                <TableHead>Codes</TableHead><TableHead>Importé le</TableHead><TableHead className="text-right">Action</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {imports.map((i) => (
                  <TableRow key={`${i.association_code}-${i.year}`} data-testid="hr-import-row">
                    <TableCell className="text-sm font-medium">{i.association_code}</TableCell>
                    <TableCell className="text-sm">{i.year}</TableCell>
                    <TableCell className="text-right tabular font-semibold">{fmtInt(Math.round(i.heures))}</TableCell>
                    <TableCell className="text-right tabular">{i.jours}</TableCell>
                    <TableCell className="max-w-[220px]">
                      <span className="flex flex-wrap gap-1">
                        {(i.codes || []).map((c) => (
                          <Badge key={c} variant="secondary" className="rounded-full font-mono text-[10px]">{c}</Badge>
                        ))}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDateTime(i.imported_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600 hover:bg-rose-50"
                        onClick={() => setToDelete(i)} data-testid="hr-delete-btn">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="hr-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet import ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les heures de {toDelete?.association_code} {toDelete?.year} ({fmtInt(Math.round(toDelete?.heures || 0))} h) seront
              retirées. La productivité live repassera à 0 pour cette période tant qu'aucun nouvel import n'est effectué.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700" data-testid="hr-delete-confirm">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>
);

function _isoWeekMonday(year, week) {
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = (jan4.getUTCDay() + 6) % 7; // Mon=0
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - jan4Dow + (week - 1) * 7);
  return monday;
}
function _isoWeekOf(date) {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const dayNum = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - dayNum + 3); // Thursday of this week
  const firstThursday = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d - firstThursday) / 86400000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
  return { year: d.getUTCFullYear(), week };
}
function _weekOptions(refYear, refWeek, count = 12) {
  const opts = [];
  const refMonday = _isoWeekMonday(refYear, refWeek);
  const fmt = (dt) => dt.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  for (let i = 0; i < count; i++) {
    const monday = new Date(refMonday);
    monday.setUTCDate(refMonday.getUTCDate() - i * 7);
    const sunday = new Date(monday);
    sunday.setUTCDate(monday.getUTCDate() + 6);
    const { year, week } = _isoWeekOf(monday);
    opts.push({ value: `${year}-${week}`, year, week, label: `S${week} ${year} · ${fmt(monday)} – ${fmt(sunday)}` });
  }
  return opts;
}

function AmnestyReportTab() {
  const { data, isLoading, refetch } = useGet('/reports/amnesty/config');
  const { data: history, refetch: refetchHistory } = useGet('/reports/amnesty/history');
  const { data: currentWeek } = useGet('/reports/amnesty/current-week');
  const [enabled, setEnabled] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [objective, setObjective] = useState('');
  const [sendWeekday, setSendWeekday] = useState('2');
  const [sendTime, setSendTime] = useState('14:30');
  const [reminderTime, setReminderTime] = useState('10:00');
  const [newEmail, setNewEmail] = useState('');
  const [weekSel, setWeekSel] = useState('');
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [forceResend, setForceResend] = useState(false);   // override « Renvoyer quand même »
  const [conflictMsg, setConflictMsg] = useState('');       // message anti-doublon (HTTP 409)

  useEffect(() => {
    if (data) {
      setEnabled(!!data.enabled);
      setRecipients(data.recipients || []);
      setObjective(String(data.annual_bs_objective ?? ''));
      if (data.send_weekday !== undefined && data.send_weekday !== null) setSendWeekday(String(data.send_weekday));
      if (data.send_time) setSendTime(data.send_time);
      if (data.reminder_time) setReminderTime(data.reminder_time);
    }
  }, [data?.enabled, JSON.stringify(data?.recipients), data?.annual_bs_objective, data?.send_weekday, data?.send_time, data?.reminder_time]);

  useEffect(() => {
    if (currentWeek?.year && currentWeek?.week && !weekSel) {
      setWeekSel(`${currentWeek.year}-${currentWeek.week}`);
    }
  }, [currentWeek?.year, currentWeek?.week]);

  const weekOptions = currentWeek?.year && currentWeek?.week
    ? _weekOptions(currentWeek.year, currentWeek.week, 12) : [];
  const selectedWeek = () => {
    const [y, w] = (weekSel || '').split('-').map((n) => parseInt(n, 10));
    return (y && w) ? { year: y, week: w } : {};
  };

  const emailValid = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e);

  const addEmail = () => {
    const e = newEmail.trim().toLowerCase();
    if (!e) return;
    if (!emailValid(e)) { toast.error('Adresse email invalide'); return; }
    if (recipients.includes(e)) { toast.info('Déjà dans la liste'); setNewEmail(''); return; }
    setRecipients([...recipients, e]);
    setNewEmail('');
  };
  const removeEmail = (e) => setRecipients(recipients.filter((x) => x !== e));

  const save = async () => {
    if (enabled && recipients.length === 0) {
      toast.error("Ajoutez au moins un destinataire avant d'activer l'envoi automatique.");
      return;
    }
    const obj = parseInt(objective, 10);
    if (!obj || obj <= 0) { toast.error('Objectif annuel invalide.'); return; }
    setSaving(true);
    try {
      await api.put('/reports/amnesty/config', {
        enabled, recipients, annual_bs_objective: obj,
        send_weekday: parseInt(sendWeekday, 10), send_time: sendTime,
        reminder_time: reminderTime,
      });
      toast.success('Configuration enregistrée.');
      refetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const sendNow = async () => {
    if (recipients.length === 0) { toast.error('Ajoutez au moins un destinataire.'); return; }
    setSending(true);
    try {
      const { data: res } = await api.post('/reports/amnesty/send-now', { ...selectedWeek(), force: forceResend });
      toast.success(`Rapport (S${res.week} ${res.year}) envoyé à ${res.to.length} destinataire(s)${res.cc?.length ? ' (copie : ' + res.cc.join(', ') + ')' : ''}.`);
      setConflictMsg('');
      setForceResend(false);
      refetch(); refetchHistory();
    } catch (e) {
      if (e?.response?.status === 409) {
        // Anti-doublon : ce rapport a déjà été envoyé cette semaine. On propose l'override.
        setConflictMsg(e.response.data?.detail || 'Ce rapport a déjà été envoyé cette semaine.');
        toast.warning('Envoi bloqué : ce rapport a déjà été envoyé cette semaine.');
      } else {
        toast.error(e?.response?.data?.detail || "Échec de l'envoi");
      }
    } finally { setSending(false); }
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const res = await api.get('/reports/amnesty/weekly', { params: selectedWeek(), responseType: 'blob' });
      const cd = res.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      const fname = m ? m[1] : 'rapport-amnesty.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.download = fname;
      document.body.appendChild(link); link.click();
      link.remove(); window.URL.revokeObjectURL(url);
      toast.success('Rapport téléchargé.');
    } catch (e) {
      toast.error('Échec du téléchargement du rapport.');
    } finally { setDownloading(false); }
  };

  const openPreview = async () => {
    setPreviewing(true);
    try {
      const { data: res } = await api.get('/reports/amnesty/email-preview', { params: selectedWeek() });
      setPreviewHtml(res.html || '');
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'aperçu du mail.");
    } finally { setPreviewing(false); }
  };

  if (isLoading) return <CardSkeleton />;
  const d = data || {};
  const triggerLabel = { auto: 'Automatique', manual: 'Manuel', test: 'Test' };
  const items = history?.items || [];
  // Libellés dynamiques synchronisés avec le réglage réel ENREGISTRÉ (jour + heure),
  // au lieu d'un texte figé — évite l'incohérence « 14h30 affiché / 12h30 configuré ».
  const WEEKDAY_FR = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
  const savedWeekday = (d.send_weekday !== undefined && d.send_weekday !== null) ? d.send_weekday : sendWeekday;
  const savedTime = d.send_time || sendTime;
  const dayLabel = WEEKDAY_FR[parseInt(savedWeekday, 10)] || 'mercredi';
  const scheduleLabel = `chaque ${dayLabel} à ${String(savedTime || '').replace(':', 'h')}`;

  return (
    <div className="space-y-4" data-testid="amnesty-report-tab">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left: activation + recipients + objective */}
        <SectionCard
          title="Reporting hebdomadaire Amnesty"
          subtitle={`Envoi automatique du fichier Excel ${scheduleLabel} (Europe/Paris), pour la semaine écoulée`}
          className="lg:col-span-2"
        >
          <div className="space-y-5">
            {!d.email_configured && (
              <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="amnesty-email-warning">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>L'envoi d'emails n'est pas encore configuré (clé Resend manquante). Contactez l'administrateur technique.</span>
              </div>
            )}

            {/* Activation toggle */}
            <div className="flex items-center justify-between rounded-2xl border bg-card p-4">
              <div className="min-w-0 pr-4">
                <p className="font-display text-sm font-semibold">Envoi automatique du {dayLabel}</p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {enabled ? `Activé — le rapport partira automatiquement ${scheduleLabel}.` : 'Désactivé — aucun envoi automatique.'}
                </p>
              </div>
              <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="amnesty-enabled-switch" />
            </div>

            {/* Schedule: day + time */}
            <div className="space-y-2">
              <Label className="text-xs">Planification de l'envoi automatique</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={sendWeekday} onValueChange={setSendWeekday}>
                  <SelectTrigger className="w-[180px]" data-testid="amnesty-weekday-select">
                    <CalendarClock className="mr-2 h-4 w-4 text-muted-foreground" />
                    <SelectValue placeholder="Jour" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="0">Lundi</SelectItem>
                    <SelectItem value="1">Mardi</SelectItem>
                    <SelectItem value="2">Mercredi</SelectItem>
                    <SelectItem value="3">Jeudi</SelectItem>
                    <SelectItem value="4">Vendredi</SelectItem>
                    <SelectItem value="5">Samedi</SelectItem>
                    <SelectItem value="6">Dimanche</SelectItem>
                  </SelectContent>
                </Select>
                <div className="relative">
                  <Clock className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="time"
                    value={sendTime}
                    onChange={(e) => setSendTime(e.target.value)}
                    className="w-[140px] pl-8"
                    data-testid="amnesty-time-input"
                  />
                </div>
                <span className="text-xs text-muted-foreground">Fuseau Europe/Paris.</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Label className="text-xs text-muted-foreground w-[180px]">Heure du rappel (la veille)</Label>
                <div className="relative">
                  <Clock className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="time"
                    value={reminderTime}
                    onChange={(e) => setReminderTime(e.target.value)}
                    className="w-[140px] pl-8"
                    data-testid="amnesty-reminder-time-input"
                  />
                </div>
                <span className="text-xs text-muted-foreground">Rappel de saisie des heures, envoyé la veille si elles manquent.</span>
              </div>
            </div>

            {/* Recipients */}
            <div className="space-y-2">
              <Label className="text-xs">Destinataires du rapport</Label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(); } }}
                  placeholder="adresse@amnesty.fr"
                  data-testid="amnesty-recipient-input"
                />
                <Button type="button" variant="outline" onClick={addEmail} className="gap-1 shrink-0" data-testid="amnesty-add-recipient-btn">
                  <Plus className="h-4 w-4" /> Ajouter
                </Button>
              </div>
              {recipients.length === 0 ? (
                <p className="rounded-xl border border-dashed p-4 text-center text-xs text-muted-foreground" data-testid="amnesty-recipients-empty">
                  Aucun destinataire. Ajoutez au moins une adresse pour activer l'envoi automatique.
                </p>
              ) : (
                <div className="flex flex-wrap gap-2" data-testid="amnesty-recipients-list">
                  {recipients.map((e) => (
                    <Badge key={e} variant="secondary" className="gap-1.5 rounded-full py-1 pl-3 pr-1.5 text-xs" data-testid="amnesty-recipient-chip">
                      <Mail className="h-3 w-3" /> {e}
                      <button type="button" onClick={() => removeEmail(e)} className="ml-0.5 rounded-full p-0.5 transition-colors hover:bg-muted-foreground/20" aria-label={`Retirer ${e}`} data-testid="amnesty-remove-recipient-btn">
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                {(d.forced_cc || []).length > 0 && (
                  <>L'adresse <span className="font-medium">{(d.forced_cc || []).join(', ')}</span> est toujours gardée en copie (CC).</>
                )}
              </p>
            </div>

            {/* Annual objective */}
            <div className="space-y-2">
              <Label className="text-xs" htmlFor="amnesty-objective-input">Objectif annuel (BS Réel)</Label>
              <div className="flex items-center gap-2">
                <div className="relative w-40">
                  <Target className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="amnesty-objective-input"
                    type="number"
                    min="1"
                    value={objective}
                    onChange={(e) => setObjective(e.target.value)}
                    className="pl-8"
                    data-testid="amnesty-objective-input"
                  />
                </div>
                <span className="text-xs text-muted-foreground">Sert à la barre de progression du rapport (onglet CUMUL MOIS).</span>
              </div>
            </div>

            {/* Report week selector (download / resend a specific week) */}
            <div className="space-y-2">
              <Label className="text-xs">Semaine du rapport</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Select value={weekSel} onValueChange={(v) => { setWeekSel(v); setConflictMsg(''); setForceResend(false); }}>
                  <SelectTrigger className="w-[300px]" data-testid="amnesty-week-select">
                    <CalendarClock className="mr-2 h-4 w-4 text-muted-foreground" />
                    <SelectValue placeholder="Choisir une semaine" />
                  </SelectTrigger>
                  <SelectContent>
                    {weekOptions.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid="amnesty-week-option">{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground">Semaine à télécharger ou renvoyer (par défaut : la semaine écoulée).</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 pt-1">
              <Button onClick={save} disabled={saving} className="gap-2 text-white" style={{ background: 'var(--tenant-accent-500)' }} data-testid="amnesty-save-config-btn">
                <Save className="h-4 w-4" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
              <Button onClick={downloadReport} disabled={downloading} variant="outline" className="gap-2" data-testid="amnesty-download-btn">
                <Download className="h-4 w-4" /> {downloading ? 'Génération…' : 'Télécharger le rapport'}
              </Button>
              <Button onClick={openPreview} disabled={previewing} variant="outline" className="gap-2" data-testid="amnesty-preview-btn">
                <Eye className="h-4 w-4" /> {previewing ? 'Génération…' : 'Aperçu du mail'}
              </Button>
              <Button onClick={sendNow} disabled={sending || !d.email_configured || recipients.length === 0 || (!!conflictMsg && !forceResend)} variant="outline" className="gap-2" data-testid="amnesty-send-now-btn">
                <Send className="h-4 w-4" /> {sending ? 'Envoi…' : (conflictMsg ? 'Renvoyer quand même' : 'Envoyer maintenant')}
              </Button>
            </div>
            {conflictMsg && (
              <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="amnesty-resend-conflict">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div className="space-y-2">
                  <p data-testid="amnesty-conflict-message">{conflictMsg}</p>
                  <label className="flex cursor-pointer items-center gap-2 font-medium" data-testid="amnesty-force-resend-label">
                    <Checkbox checked={forceResend} onCheckedChange={(v) => setForceResend(!!v)} data-testid="amnesty-force-resend-checkbox" />
                    Renvoyer quand même
                  </label>
                </div>
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              « Télécharger le rapport » génère le fichier de la semaine choisie pour vérification avant envoi. « Aperçu du mail » montre le rendu (récapitulatif, anecdote, mise en forme) sans rien envoyer. « Envoyer maintenant » l'expédie immédiatement aux destinataires ci-dessus. Pensez à <span className="font-medium">Enregistrer</span> avant.
            </p>
          </div>
        </SectionCard>

        {/* Right: summary */}
        <SectionCard title="Détails de l'envoi" subtitle="Paramètres actuels">
          <div className="space-y-3 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Statut</span>
              {enabled
                ? <Badge className="rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" data-testid="amnesty-status-badge">Actif</Badge>
                : <Badge variant="secondary" className="rounded-full" data-testid="amnesty-status-badge">Inactif</Badge>}
            </div>
            <div className="flex items-start justify-between gap-3">
              <span className="text-muted-foreground">Expéditeur</span>
              <span className="text-right font-medium" data-testid="amnesty-sender">{d.sender || '—'}</span>
            </div>
            <div className="flex items-start justify-between gap-3">
              <span className="text-muted-foreground">Planification</span>
              <span className="text-right">{d.schedule || '—'}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Objectif annuel (BS)</span>
              <span className="font-medium tabular" data-testid="amnesty-objective-summary">{fmtInt(parseInt(objective, 10) || 0)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Envoi email</span>
              {d.email_configured
                ? <span className="inline-flex items-center gap-1 text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Configuré</span>
                : <span className="inline-flex items-center gap-1 text-amber-600"><AlertTriangle className="h-4 w-4" /> Non configuré</span>}
            </div>
            <div className="flex items-start justify-between gap-3 border-t pt-3">
              <span className="text-muted-foreground">Dernier envoi auto</span>
              <span className="text-right" data-testid="amnesty-last-sent">{d.last_sent_at ? fmtDateTime(d.last_sent_at) : 'Jamais'}</span>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* History */}
      <SectionCard title="Historique des envois" subtitle="30 derniers rapports envoyés (automatiques, manuels et tests)">
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed py-10 text-center" data-testid="amnesty-history-empty">
            <History className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Aucun envoi enregistré pour le moment.</p>
          </div>
        ) : (
          <div className="overflow-x-auto" data-testid="amnesty-history-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Semaine</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Destinataires</TableHead>
                  <TableHead>Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.id} data-testid="amnesty-history-row">
                    <TableCell className="whitespace-nowrap text-sm">{it.sent_at ? fmtDateTime(it.sent_at) : '—'}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">S{it.week} · {it.year}</TableCell>
                    <TableCell>
                      <Badge variant={it.trigger === 'auto' ? 'default' : 'secondary'} className="rounded-full text-xs">
                        {triggerLabel[it.trigger] || it.trigger}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex flex-col">
                        <span>{(it.recipients || []).join(', ') || '—'}</span>
                        {(it.cc || []).length > 0 && (
                          <span className="text-xs text-muted-foreground">Copie : {(it.cc || []).join(', ')}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {it.status === 'sent'
                        ? <span className="inline-flex items-center gap-1 text-sm text-emerald-600"><CheckCircle2 className="h-4 w-4" /> Envoyé</span>
                        : <span className="inline-flex items-center gap-1 text-sm text-red-600"><AlertTriangle className="h-4 w-4" /> {it.status || 'Erreur'}</span>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </SectionCard>

      {/* Email preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl" data-testid="amnesty-preview-dialog">
          <DialogHeader><DialogTitle>Aperçu du mail de reporting</DialogTitle></DialogHeader>
          <p className="-mt-2 text-xs text-muted-foreground">
            Le récapitulatif et l'anecdote sont générés automatiquement. Cet aperçu est un exemple : le mail réellement envoyé peut varier (nouvelle anecdote à chaque envoi).
          </p>
          <div className="overflow-hidden rounded-lg border bg-muted/30">
            <iframe
              title="Aperçu du mail de reporting"
              srcDoc={previewHtml}
              className="w-full"
              style={{ height: '68vh', border: 0, background: '#f4f5f7' }}
              data-testid="amnesty-preview-iframe"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPreviewOpen(false)} data-testid="amnesty-preview-close">Fermer</Button>
            <Button variant="outline" onClick={openPreview} disabled={previewing} className="gap-2" data-testid="amnesty-preview-regenerate">
              <RefreshCw className="h-4 w-4" /> {previewing ? 'Génération…' : 'Régénérer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

